import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowsRightLeftIcon,
  CurrencyDollarIcon,
  TvIcon,
} from '@heroicons/react/24/outline';
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { dashboardAPI, transactionsAPI } from '../services/api';
import { API_BASE_URL } from '../config/apiBaseUrl';
import StatusToast from './StatusToast';
import usePersistentUiState from '../hooks/usePersistentUiState';
import { getBusinessDayStampUtc, parseBusinessDate, toBusinessIsoDate } from '../utils/businessDate';

const DEFAULT_COLOR = '#64748b';
const INITIAL_VISIBLE_TRANSACTIONS = 5;
const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];
const COLLAPSIBLE_SECTION_IDS = [
  'upcoming-reminders',
  'assistant',
  'key-insights',
  'spending-by-category',
  'month-over-month',
  'recent-transactions',
];
const RECURRING_REMINDER_STORAGE_KEY = 'receipt-tracker:recurring-reminders:v1';
const RECURRING_REMINDER_ACTIONS_STORAGE_KEY = 'receipt-tracker:recurring-reminder-actions:v1';
const DASHBOARD_UI_STATE_KEY = 'receipt-tracker:ui:dashboard:v1';
const REMINDER_DAY_OPTIONS = [1, 2, 3, 7];
const SHOW_SPENDING_AI = false;
const ENABLE_REMINDER_TEST_CONTROLS = process.env.NODE_ENV !== 'production';

const readReminderStorageState = (storageKey) => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return {};
    }
    const parsedValue = JSON.parse(rawValue);
    return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
      ? parsedValue
      : {};
  } catch (storageError) {
    console.error(`Failed to load reminder storage state for ${storageKey}:`, storageError);
    return {};
  }
};

const buildInitialCollapsedSections = () => COLLAPSIBLE_SECTION_IDS.reduce((accumulator, sectionId) => ({
  ...accumulator,
  [sectionId]: false,
}), {});

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const buildDefaultDashboardUiState = () => ({
  selectedMonth: buildCurrentMonthValue(),
  collapsedSections: buildInitialCollapsedSections(),
  upcomingView: 'month',
});

const sanitizeDashboardUiState = (value, fallbackState = buildDefaultDashboardUiState()) => {
  const nextState = isPlainObject(value) ? value : {};
  const selectedMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(String(nextState.selectedMonth || ''))
    ? String(nextState.selectedMonth)
    : fallbackState.selectedMonth;
  const collapsedSections = buildInitialCollapsedSections();

  if (isPlainObject(nextState.collapsedSections)) {
    COLLAPSIBLE_SECTION_IDS.forEach((sectionId) => {
      collapsedSections[sectionId] = Boolean(nextState.collapsedSections[sectionId]);
    });
  }

  return {
    ...fallbackState,
    selectedMonth,
    collapsedSections,
    upcomingView: String(nextState.upcomingView || '') === 'week' ? 'week' : 'month',
  };
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount || 0));
};

const formatCurrencyRounded = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(Number(amount || 0)));
};

const formatSavingsRounded = (amount) => {
  const numeric = Number(amount || 0);
  const rounded = Math.round(Math.abs(numeric));
  const formatted = formatCurrencyRounded(rounded);
  return numeric < 0 ? `(${formatted})` : formatted;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hexToRgba = (hexColor, alpha = 1) => {
  const normalized = String(hexColor || '').trim();
  const fallbackAlpha = Math.max(0, Math.min(1, Number(alpha) || 1));
  if (!normalized.startsWith('#')) {
    return `rgba(100, 116, 139, ${fallbackAlpha})`;
  }

  const hex = normalized.slice(1);
  const isShort = hex.length === 3;
  const isFull = hex.length === 6;
  if (!isShort && !isFull) {
    return `rgba(100, 116, 139, ${fallbackAlpha})`;
  }

  const expanded = isShort
    ? hex.split('').map((char) => `${char}${char}`).join('')
    : hex;
  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);

  if ([r, g, b].some((value) => Number.isNaN(value))) {
    return `rgba(100, 116, 139, ${fallbackAlpha})`;
  }

  return `rgba(${r}, ${g}, ${b}, ${fallbackAlpha})`;
};

const toComparableText = (value) => String(value || '').toLowerCase().trim();
const toIsoDate = (value) => toBusinessIsoDate(value);

const buildReceiptKey = (transaction) => {
  if (transaction.receiptImageUrl) {
    return `receipt:${transaction.receiptImageUrl}`;
  }

  const fallbackDate = toIsoDate(transaction.transactionDate) || 'unknown-date';
  const fallbackStore = toComparableText(transaction.storeName) || 'unknown-store';
  const fallbackLocation = toComparableText(transaction.storeLocation) || 'unknown-location';
  const fallbackPayment = toComparableText(transaction.paymentMethod) || 'unknown-payment';

  return `meta:${fallbackDate}|${fallbackStore}|${fallbackLocation}|${fallbackPayment}`;
};

const parseTransactionDate = (dateValue) => {
  const parsed = parseBusinessDate(dateValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDate = (dateString) => {
  const date = parseTransactionDate(dateString);
  if (!date) {
    return '--';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const getTransactionCategoryLabel = (transaction) => {
  if (!transaction?.category) {
    return 'Uncategorized';
  }

  return transaction.category.parentCategory?.name
    || transaction.category.name
    || 'Uncategorized';
};

const buildCategorySummary = (transactions = []) => {
  const summaryByCategory = new Map();

  transactions.forEach((transaction) => {
    const category = getTransactionCategoryLabel(transaction);
    const key = toComparableText(category) || 'uncategorized';
    const transactionDate = parseTransactionDate(transaction?.transactionDate);
    const transactionTime = transactionDate ? transactionDate.getTime() : null;
    const storeName = String(transaction?.storeName || '').trim() || 'Unknown store';

    if (!summaryByCategory.has(key)) {
      summaryByCategory.set(key, {
        category,
        totalAmount: 0,
        latestTransactionTime: null,
        latestStoreName: 'Unknown store',
      });
    }

    const entry = summaryByCategory.get(key);
    entry.totalAmount += toNumber(transaction?.itemTotal);
    if (transactionTime && (entry.latestTransactionTime === null || transactionTime > entry.latestTransactionTime)) {
      entry.latestTransactionTime = transactionTime;
      entry.latestStoreName = storeName;
    }
  });

  return Array.from(summaryByCategory.values())
    .map((entry) => ({
      ...entry,
      latestTransactionDate: entry.latestTransactionTime
        ? new Date(entry.latestTransactionTime).toISOString()
        : null,
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) {
        return b.totalAmount - a.totalAmount;
      }
      return a.category.localeCompare(b.category);
    });
};

const formatMonthLabel = (monthValue) => {
  const [year, month] = monthValue.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const renderCategoryLabel = (name) => {
  const normalized = String(name || '').trim();
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length === 2) {
    return (
      <>
        {words[0]}
        <br />
        {words[1]}
      </>
    );
  }
  return normalized || 'Uncategorized';
};

const shiftMonthValue = (monthValue, deltaMonths) => {
  const [year, month] = monthValue.split('-').map(Number);
  const nextDate = new Date(year, month - 1 + deltaMonths, 1);
  return `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
};

const buildCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const getLocalDayStamp = (dateValue) => {
  return getBusinessDayStampUtc(dateValue);
};

export const getDaysUntilLocalDate = (targetDateValue, nowValue = new Date()) => {
  const targetStamp = getLocalDayStamp(targetDateValue);
  if (targetStamp === null) {
    return null;
  }

  const now = nowValue instanceof Date ? nowValue : new Date(nowValue);
  if (Number.isNaN(now.getTime())) {
    return null;
  }

  const startOfTodayLocal = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((targetStamp - startOfTodayLocal) / 86400000);
};

const formatRecurrenceFrequency = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'bi-weekly') {
    return 'Bi-weekly';
  }
  if (normalized === 'weekly') {
    return 'Weekly';
  }
  if (normalized === 'monthly') {
    return 'Monthly';
  }
  return 'Recurring';
};

const getSelectedReminderDays = (setting) => {
  if (!setting || typeof setting !== 'object') {
    return null;
  }

  const reminderDays = Number(setting.reminderDays);
  if (Number.isFinite(reminderDays) && REMINDER_DAY_OPTIONS.includes(reminderDays)) {
    return reminderDays;
  }

  const legacyDaysBefore = Number(setting.daysBefore);
  if (Boolean(setting.enabled) && Number.isFinite(legacyDaysBefore) && REMINDER_DAY_OPTIONS.includes(legacyDaysBefore)) {
    return legacyDaysBefore;
  }

  return null;
};

const normalizeUpcomingRecurringEntries = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.upcomingRecurring)) {
    return value.upcomingRecurring;
  }
  return [];
};

const postRecurringPaymentDirect = async (recurringTransactionId, occurrenceDate) => {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : '';
  const response = await fetch(
    `${API_BASE_URL}/api/transactions/recurring/${encodeURIComponent(recurringTransactionId)}/record-payment`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ occurrenceDate }),
    }
  );

  const responseData = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(responseData?.error || 'Failed to record recurring payment');
  }

  return responseData;
};

const buildDefaultSnoozeDraft = () => {
  const nextDay = new Date();
  nextDay.setDate(nextDay.getDate() + 1);
  const yyyy = nextDay.getFullYear();
  const mm = String(nextDay.getMonth() + 1).padStart(2, '0');
  const dd = String(nextDay.getDate()).padStart(2, '0');
  return {
    date: `${yyyy}-${mm}-${dd}`,
    time: '09:00',
  };
};

const normalizeTimeToQuarterHour = (rawTimeValue) => {
  const raw = String(rawTimeValue || '').trim() || '09:00';
  const [hourPart, minutePart] = raw.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  const safeHour = Number.isFinite(hour) ? Math.min(Math.max(hour, 0), 23) : 9;
  const safeMinute = Number.isFinite(minute) ? Math.min(Math.max(minute, 0), 59) : 0;
  const quarterMinute = Math.round(safeMinute / 15) * 15;
  const normalizedMinute = quarterMinute >= 60 ? 0 : quarterMinute;
  const normalizedHour = quarterMinute >= 60 ? (safeHour + 1) % 24 : safeHour;
  return `${String(normalizedHour).padStart(2, '0')}:${String(normalizedMinute).padStart(2, '0')}`;
};

const getTimePickerParts = (rawTimeValue) => {
  const normalized = normalizeTimeToQuarterHour(rawTimeValue);
  const [hourRaw, minuteRaw] = normalized.split(':');
  const hour24 = Number(hourRaw);
  const minute = Number(minuteRaw);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return {
    hour: String(hour12).padStart(2, '0'),
    minute: String(minute).padStart(2, '0'),
    period,
  };
};

const buildTimeFromPickerParts = ({ hour, minute, period }) => {
  const hour12 = Math.min(Math.max(Number(hour || 12), 1), 12);
  const minuteValue = ['00', '15', '30', '45'].includes(String(minute)) ? String(minute) : '00';
  const normalizedPeriod = String(period || 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';
  const hour24 = normalizedPeriod === 'PM' ? (hour12 % 12) + 12 : (hour12 % 12);
  return `${String(hour24).padStart(2, '0')}:${minuteValue}`;
};

const formatDateForInputDisplay = (rawDate) => {
  const raw = String(rawDate || '').trim();
  if (!raw) {
    return 'Select date';
  }
  const parts = raw.split('-');
  if (parts.length !== 3) {
    return raw;
  }
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
};

const CustomCenterLabel = ({ viewBox, value, label, rotation, compact = false }) => {
  if (!viewBox) {
    return null;
  }

  const { cx, cy } = viewBox;

  return (
    <g transform={`rotate(${-rotation} ${cx} ${cy})`}>
      <text x={cx} y={cy - 4} textAnchor="middle" fill="#0f172a" fontSize={compact ? 24 : 36} fontWeight="700">
        {value}
      </text>
      <text x={cx} y={cy + (compact ? 16 : 24)} textAnchor="middle" fill="#475569" fontSize={compact ? 12 : 16} fontWeight="500">
        {label}
      </text>
    </g>
  );
};

const SegmentPercentageLabel = ({
  cx, cy, midAngle, innerRadius, outerRadius, percent,
}) => {
  const pct = Number(percent || 0) * 100;
  if (pct < 4) {
    return null;
  }

  const radius = innerRadius + (outerRadius - innerRadius) * 0.52;
  const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

  return (
    <text
      x={x}
      y={y}
      fill="#ffffff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="20"
      fontWeight="400"
      style={{ pointerEvents: 'none' }}
    >
      {`${pct.toFixed(0)}%`}
    </text>
  );
};

const SpendingByCategorySection = memo(function SpendingByCategorySection({
  sectionRef,
  isCollapsed,
  handleSectionCardClick,
  renderSectionToggle,
  topWheelCategories,
  chartSizing,
  wheelChartData,
  formatCurrency,
  formatDate,
  summaryTotalSpent,
  isSmallScreen,
  selectedMonth,
  transactionsMonthLink,
}) {
  const [rotation, setRotation] = useState(0);
  const [expandedSpendingCategoryId, setExpandedSpendingCategoryId] = useState(null);
  const [expandedSubcategoryId, setExpandedSubcategoryId] = useState(null);
  const wheelRef = useRef(null);
  const spendingListRef = useRef(null);
  const pendingSpendingAnchorRef = useRef(null);
  const rotationRef = useRef(0);
  const velocityRef = useRef(0);
  const isDraggingRef = useRef(false);
  const lastAngleRef = useRef(0);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(null);

  const captureSpendingListAnchor = useCallback((clickedElement) => {
    const container = spendingListRef.current;
    if (!container || !clickedElement) {
      return;
    }

    pendingSpendingAnchorRef.current = {
      element: clickedElement,
      top: clickedElement.getBoundingClientRect().top,
    };
  }, []);

  useEffect(() => {
    setExpandedSpendingCategoryId(null);
    setExpandedSubcategoryId(null);
    rotationRef.current = 0;
    velocityRef.current = 0;
    setRotation(0);
  }, [selectedMonth]);

  useEffect(() => {
    setExpandedSubcategoryId(null);
  }, [expandedSpendingCategoryId]);

  useEffect(() => {
    const pendingAnchor = pendingSpendingAnchorRef.current;
    const container = spendingListRef.current;

    if (!pendingAnchor || !container) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const anchorElement = pendingAnchor.element;
      if (!anchorElement || !anchorElement.isConnected) {
        pendingSpendingAnchorRef.current = null;
        return;
      }

      const nextTop = anchorElement.getBoundingClientRect().top;
      const delta = nextTop - pendingAnchor.top;
      if (Math.abs(delta) > 0.5) {
        container.scrollTop += delta;
      }

      pendingSpendingAnchorRef.current = null;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [expandedSpendingCategoryId, expandedSubcategoryId]);

  const stopInertia = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startInertia = useCallback(() => {
    stopInertia();

    const step = () => {
      rotationRef.current += velocityRef.current;
      velocityRef.current *= 0.98;
      setRotation(rotationRef.current);

      if (Math.abs(velocityRef.current) < 0.01) {
        velocityRef.current = 0;
        rafRef.current = null;
        return;
      }

      rafRef.current = requestAnimationFrame(step);
    };

    if (Math.abs(velocityRef.current) >= 0.01) {
      rafRef.current = requestAnimationFrame(step);
    }
  }, [stopInertia]);

  const getPointerAngle = useCallback((clientX, clientY) => {
    const node = wheelRef.current;
    if (!node) {
      return 0;
    }

    const rect = node.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI;
  }, []);

  const normalizeDeltaAngle = useCallback((delta) => {
    let next = delta;
    while (next > 180) {
      next -= 360;
    }
    while (next < -180) {
      next += 360;
    }
    return next;
  }, []);

  const handlePointerDown = useCallback((event) => {
    event.preventDefault();
    stopInertia();
    velocityRef.current = 0;
    isDraggingRef.current = true;
    lastAngleRef.current = getPointerAngle(event.clientX, event.clientY);
    lastTimeRef.current = performance.now();
  }, [getPointerAngle, stopInertia]);

  const handlePointerMove = useCallback((event) => {
    if (!isDraggingRef.current) {
      return;
    }

    const now = performance.now();
    const angle = getPointerAngle(event.clientX, event.clientY);
    const delta = normalizeDeltaAngle(angle - lastAngleRef.current);
    const dt = now - lastTimeRef.current;

    rotationRef.current += delta;
    setRotation(rotationRef.current);

    if (dt > 0) {
      velocityRef.current = delta / (dt / 16.67);
    }

    lastAngleRef.current = angle;
    lastTimeRef.current = now;
  }, [getPointerAngle, normalizeDeltaAngle]);

  const handlePointerUp = useCallback(() => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    startInertia();
  }, [startInertia]);

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      stopInertia();
    };
  }, [handlePointerMove, handlePointerUp, stopInertia]);

  return (
    <section
      ref={sectionRef}
      onClick={(event) => handleSectionCardClick('spending-by-category', event)}
      className={`group relative overflow-hidden border border-white/80 bg-gradient-to-br from-white via-teal-50/90 to-cyan-100/55 backdrop-blur shadow-[0_14px_34px_-24px_rgba(6,182,212,0.45)] transition-all duration-200 ${isCollapsed ? 'rounded-2xl p-3 md:p-4 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-24px_rgba(6,182,212,0.52)]' : 'rounded-3xl p-5 md:p-6'}`}
    >
      <div className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/90 to-transparent" />
      <div className={`${isCollapsed ? 'mb-0' : 'mb-5'} flex items-center justify-between gap-3`}>
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="h-6 w-2 rounded-full bg-gradient-to-b from-teal-500 to-cyan-500 shadow-[0_0_0_2px_rgba(255,255,255,0.7)]" aria-hidden="true" />
          <h2 className={`${isCollapsed ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl'} font-semibold tracking-tight text-slate-900`}>Spending by Category</h2>
        </div>
        {renderSectionToggle('spending-by-category', isCollapsed, 'Spending by Category')}
      </div>

      {!isCollapsed && (topWheelCategories.length > 0 ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-center">
          <div className={chartSizing.containerHeightClass}>
            <div
              ref={wheelRef}
              className="h-full w-full cursor-grab active:cursor-grabbing touch-none select-none"
              onPointerDown={handlePointerDown}
              style={{ transform: `rotate(${rotation}deg)`, transformOrigin: 'center center', willChange: 'transform' }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={wheelChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={chartSizing.innerRadius}
                    outerRadius={chartSizing.outerRadius}
                    paddingAngle={1}
                    dataKey="value"
                    stroke="#ffffff"
                    strokeWidth={2}
                    label={SegmentPercentageLabel}
                    labelLine={false}
                  >
                    {wheelChartData.map((entry) => (
                      <Cell key={entry.id} fill={entry.color || DEFAULT_COLOR} />
                    ))}
                    <Label
                      content={({ viewBox }) => (
                        <CustomCenterLabel
                          viewBox={viewBox}
                          value="100%"
                          label={formatCurrency(summaryTotalSpent)}
                          rotation={rotation}
                          compact={isSmallScreen}
                        />
                      )}
                      position="center"
                    />
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(_, payload) => {
                      const item = payload?.[0]?.payload;
                      if (!item) {
                        return '';
                      }

                      return `${item.name} - ${Number(item.percentage || 0).toFixed(1)}%`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div ref={spendingListRef} className="space-y-3 lg:max-h-[28rem] lg:overflow-y-auto lg:pr-1">
            {topWheelCategories.map((category) => {
              const isExpanded = expandedSpendingCategoryId === category.id;
              const hasChildren = Array.isArray(category.children) && category.children.length > 0;

              return (
                <div key={category.id} className="rounded-xl border border-slate-200 bg-white">
                  <button
                    type="button"
                    onClick={(event) => {
                      captureSpendingListAnchor(event.currentTarget);
                      if (isExpanded) {
                        setExpandedSpendingCategoryId(null);
                        setExpandedSubcategoryId(null);
                        return;
                      }
                      setExpandedSpendingCategoryId(category.id);
                      setExpandedSubcategoryId(null);
                    }}
                    className="w-full px-3 py-2.5 flex items-center gap-3 text-left"
                  >
                    <span
                      className="h-3.5 w-3.5 rounded-full"
                      style={{ backgroundColor: category.color || DEFAULT_COLOR }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-800 font-medium truncate">{category.name}</p>
                      <p className="text-xs text-slate-500">{category.count} transactions</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-slate-900 font-semibold">{formatCurrency(category.value)}</p>
                      <p className="text-xs text-slate-500">{Number(category.percentage || 0).toFixed(1)}%</p>
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden border-slate-100 px-3 transition-all duration-500 ease-in-out ${
                      isExpanded ? 'max-h-[2000px] border-t py-2.5 opacity-100' : 'max-h-0 border-t-0 py-0 opacity-0'
                    }`}
                  >
                    <div className="space-y-2">
                      {hasChildren ? (
                        category.children.map((child) => {
                          const isSubcategoryExpanded = expandedSubcategoryId === child.id;
                          const childTransactions = Array.isArray(child.transactions) ? child.transactions : [];
                          const childColor = child.color || DEFAULT_COLOR;

                          return (
                            <div
                              key={`wheel-child-${category.id}-${child.id}`}
                              className="rounded-lg border border-slate-200/90 bg-white"
                              style={{ boxShadow: `inset 3px 0 0 ${hexToRgba(childColor, 0.35)}` }}
                            >
                              <button
                                type="button"
                                onClick={(event) => {
                                  captureSpendingListAnchor(event.currentTarget);
                                  setExpandedSubcategoryId(isSubcategoryExpanded ? null : child.id);
                                }}
                                className="w-full px-2.5 py-1.5 text-left"
                              >
                                <div className="grid grid-cols-[1fr_auto] items-start gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-800 truncate">{child.name}</p>
                                    <div className="mt-1 flex items-center gap-1.5">
                                      <span className="inline-flex rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                                        {Number(child.count || 0)} items
                                      </span>
                                      <span className="inline-flex rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                                        {Number(child.percentageWithinParent || 0).toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-semibold text-slate-900">{formatCurrency(child.value)}</p>
                                    <p className="text-xs text-slate-400" aria-hidden="true">
                                      {isSubcategoryExpanded ? '-' : '+'}
                                    </p>
                                  </div>
                                </div>
                              </button>

                              <div
                                className={`overflow-hidden border-slate-200 px-2.5 transition-all duration-500 ease-in-out ${
                                  isSubcategoryExpanded ? 'max-h-[1200px] border-t py-2 opacity-100' : 'max-h-0 border-t-0 py-0 opacity-0'
                                }`}
                                style={isSubcategoryExpanded ? { backgroundColor: hexToRgba(childColor, 0.04) } : undefined}
                              >
                                <div className="space-y-1.5">
                                  <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_auto_auto] lg:grid-cols-[minmax(0,1fr)_auto_minmax(10rem,1fr)_auto] items-center gap-3 px-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                    <p>Item</p>
                                    <p>Date</p>
                                    <p className="hidden lg:block">Store</p>
                                    <p className="text-right">Amount</p>
                                  </div>
                                  {childTransactions.length > 0 ? (
                                    childTransactions.map((item, index) => (
                                      <div
                                        key={`child-item-${child.id}-${item.id || index}`}
                                        className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 rounded-md bg-white/90 px-2 py-1.5 sm:grid-cols-[minmax(0,1fr)_auto_auto] lg:grid-cols-[minmax(0,1fr)_auto_minmax(10rem,1fr)_auto] sm:items-center"
                                        style={{
                                          boxShadow: `inset 2px 0 0 ${hexToRgba(childColor, 0.4)}, inset 0 0 0 1px ${hexToRgba(childColor, 0.08)}`,
                                        }}
                                      >
                                        <p className="min-w-0 truncate text-xs font-medium text-slate-800">
                                          {String(item.itemName || '').trim() || 'Unnamed item'}
                                        </p>
                                        <p className="col-span-2 min-w-0 truncate text-[11px] text-slate-600 sm:col-span-1 sm:whitespace-nowrap lg:hidden">
                                          {formatDate(item.transactionDate)}
                                          {String(item.storeName || '').trim() ? ` | ${String(item.storeName).trim()}` : ''}
                                        </p>
                                        <p className="hidden lg:block text-[11px] text-slate-600 whitespace-nowrap">
                                          {formatDate(item.transactionDate)}
                                        </p>
                                        <p className="hidden lg:block min-w-0 truncate text-[11px] text-slate-600">
                                          {String(item.storeName || '').trim() || 'Unknown store'}
                                        </p>
                                        <p className="row-start-1 col-start-2 text-xs font-semibold text-slate-900 whitespace-nowrap text-right sm:row-auto sm:col-start-3 lg:col-start-4">
                                          {formatCurrency(item.itemTotal)}
                                        </p>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="px-2 text-xs text-slate-500">No items for this subcategory.</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-slate-500">No child categories in this parent.</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="h-48 rounded-2xl bg-slate-50 border border-slate-200 grid place-items-center text-slate-500">
          No transactions for this month
        </div>
      ))}
      {!isCollapsed && (
        <div className="mt-3 flex justify-end">
          <Link to={transactionsMonthLink} className="text-sm font-medium lowercase text-blue-700 hover:text-blue-800">
            view all
          </Link>
        </div>
      )}
    </section>
  );
});

const RecentTransactionsSection = memo(function RecentTransactionsSection({
  sectionRef,
  isCollapsed,
  handleSectionCardClick,
  renderSectionToggle,
  recentTransactions,
  formatDate,
  formatCurrency,
}) {
  const groupedRecentTransactions = useMemo(() => {
    const groups = new Map();

    recentTransactions.forEach((transaction) => {
      const key = buildReceiptKey(transaction);
      const amount = toNumber(transaction.itemTotal);
      const transactionTime = new Date(transaction.transactionDate).getTime();

      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          storeName: transaction.storeName,
          transactionDate: transaction.transactionDate,
          itemTotal: 0,
          category: transaction.category || null,
          categoryTotals: new Map(),
        });
      }

      const group = groups.get(key);
      group.itemTotal = Number((group.itemTotal + amount).toFixed(2));

      const currentGroupTime = new Date(group.transactionDate).getTime();
      if (Number.isFinite(transactionTime) && (!Number.isFinite(currentGroupTime) || transactionTime > currentGroupTime)) {
        group.transactionDate = transaction.transactionDate;
      }

      const categoryId = transaction.category?.id;
      if (categoryId) {
        const currentCategoryTotal = group.categoryTotals.get(categoryId) || 0;
        group.categoryTotals.set(categoryId, currentCategoryTotal + amount);
      }
    });

    return Array.from(groups.values())
      .map((group) => {
        if (group.categoryTotals.size <= 1) {
          return group;
        }

        return {
          ...group,
          category: {
            id: 'multiple-categories',
            name: 'Multiple categories',
            color: DEFAULT_COLOR,
          },
        };
      })
      .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
  }, [recentTransactions]);

  const visibleRecentTransactions = useMemo(
    () => groupedRecentTransactions.slice(0, INITIAL_VISIBLE_TRANSACTIONS),
    [groupedRecentTransactions]
  );

  return (
    <section
      ref={sectionRef}
      onClick={(event) => handleSectionCardClick('recent-transactions', event)}
      className={`group relative overflow-hidden border border-white/80 bg-gradient-to-br from-white via-amber-50/90 to-orange-100/55 backdrop-blur shadow-[0_14px_34px_-24px_rgba(249,115,22,0.45)] transition-all duration-200 ${isCollapsed ? 'rounded-2xl p-3 md:p-4 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-24px_rgba(249,115,22,0.52)]' : 'rounded-3xl p-5 md:p-6'}`}
    >
      <div className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-orange-300/90 to-transparent" />
      <div className={`${isCollapsed ? 'mb-0' : 'mb-4'} flex items-center justify-between gap-3`}>
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="h-6 w-2 rounded-full bg-gradient-to-b from-orange-500 to-amber-500 shadow-[0_0_0_2px_rgba(255,255,255,0.7)]" aria-hidden="true" />
          <h2 className={`${isCollapsed ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl'} font-semibold tracking-tight text-slate-900`}>Recent Transactions</h2>
        </div>
        {renderSectionToggle('recent-transactions', isCollapsed, 'Recent Transactions')}
      </div>

      {!isCollapsed && (groupedRecentTransactions.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 py-12 text-center text-slate-500">
          No transactions found
        </div>
      ) : (
        <>
          <div className="sm:hidden divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {visibleRecentTransactions.map((transaction) => {
              const amount = Number(transaction.itemTotal || 0);
              const amountColor = amount >= 0 ? 'text-emerald-600' : 'text-rose-600';

              return (
                <article key={transaction.id} className="px-2.5 py-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{transaction.storeName || 'Unknown store'}</p>
                      <p className="text-[10px] text-slate-600">{formatDate(transaction.transactionDate)}</p>
                    </div>
                    <p className={`text-sm font-semibold whitespace-nowrap ${amountColor}`}>
                      {amount >= 0 ? '+' : '-'}
                      {formatCurrency(Math.abs(amount))}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-3 font-semibold">Store</th>
                  <th className="py-3 px-3 font-semibold">Date</th>
                  <th className="py-3 pl-3 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {visibleRecentTransactions.map((transaction) => {
                  const amount = Number(transaction.itemTotal || 0);
                  const amountColor = amount >= 0 ? 'text-emerald-600' : 'text-rose-600';

                  return (
                    <tr key={transaction.id} className="border-b border-slate-100">
                      <td className="py-4 pr-3">
                        <p className="font-semibold text-slate-900">{transaction.storeName || 'Unknown store'}</p>
                      </td>
                      <td className="py-4 px-3 text-slate-700">{formatDate(transaction.transactionDate)}</td>
                      <td className={`py-4 pl-3 text-right font-semibold ${amountColor}`}>
                        {amount >= 0 ? '+' : '-'}
                        {formatCurrency(Math.abs(amount))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ))}
      {!isCollapsed && (
        <div className="mt-3 flex justify-end">
          <Link to="/transactions" className="text-sm font-medium lowercase text-blue-700 hover:text-blue-800">
            view all
          </Link>
        </div>
      )}
    </section>
  );
});

const UpcomingRemindersSection = memo(function UpcomingRemindersSection({
  sectionRef,
  isCollapsed,
  handleSectionCardClick,
  renderSectionToggle,
  upcomingRecurring,
  upcomingRecurringLoading,
  upcomingRecurringError,
  reminderSettingsBySeries,
  reminderActionsByOccurrence,
  setReminderActionsByOccurrence,
  updateRecurringReminderSetting,
  upcomingView,
  setUpcomingView,
  selectedMonth,
  selectedMonthNumber,
  selectedYear,
  setCollapsedSections,
  recordRecurringPayment,
  formatDate,
  formatCurrency,
}) {
  const [snoozeDraftByOccurrence, setSnoozeDraftByOccurrence] = useState({});
  const [isReminderMenuOpenByOccurrence, setIsReminderMenuOpenByOccurrence] = useState({});
  const [forcedDueReminderIds, setForcedDueReminderIds] = useState([]);
  const [isDueReminderModalOpen, setIsDueReminderModalOpen] = useState(false);
  const [dismissedDueReminderSignature, setDismissedDueReminderSignature] = useState('');
  const [isRecordingPaymentByOccurrence, setIsRecordingPaymentByOccurrence] = useState({});
  const snoozeDateInputRefs = useRef({});
  const hasInitializedReminderUiRef = useRef(false);

  const thisWeekUpcomingRecurring = useMemo(() => {
    const now = new Date();
    const todayStamp = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = now.getDay();
    const weekStartStamp = todayStamp - dayOfWeek * 86400000;
    const weekEndStamp = weekStartStamp + 6 * 86400000;

    return upcomingRecurring.filter((entry) => {
      const targetStamp = getLocalDayStamp(entry?.transactionDate);
      if (targetStamp === null) {
        return false;
      }
      return targetStamp >= todayStamp && targetStamp <= weekEndStamp;
    });
  }, [upcomingRecurring]);

  const thisMonthUpcomingRecurring = useMemo(() => {
    const now = new Date();
    const todayStamp = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStartStamp = Date.UTC(now.getFullYear(), now.getMonth(), 1);
    const monthEndStamp = Date.UTC(now.getFullYear(), now.getMonth() + 1, 0);

    return upcomingRecurring.filter((entry) => {
      const targetStamp = getLocalDayStamp(entry?.transactionDate);
      if (targetStamp === null) {
        return false;
      }
      return targetStamp >= Math.max(todayStamp, monthStartStamp) && targetStamp <= monthEndStamp;
    });
  }, [upcomingRecurring]);

  const activeUpcomingRecurring = upcomingView === 'week'
    ? thisWeekUpcomingRecurring
    : thisMonthUpcomingRecurring;

  const visibleUpcomingRecurring = useMemo(() => (
    activeUpcomingRecurring.filter((entry) => reminderActionsByOccurrence[entry.id]?.status !== 'paid')
  ), [activeUpcomingRecurring, reminderActionsByOccurrence]);

  const baseDueRecurringReminders = useMemo(() => (
    upcomingRecurring
      .filter((entry) => {
        const seriesId = String(entry?.recurringTransactionId || '').trim();
        if (!seriesId) {
          return false;
        }

        const savedSetting = reminderSettingsBySeries[seriesId];
        const selectedDaysBefore = getSelectedReminderDays(savedSetting);
        if (selectedDaysBefore === null) {
          return false;
        }

        const daysUntil = getDaysUntilLocalDate(entry.transactionDate);
        return daysUntil !== null && daysUntil === selectedDaysBefore;
      })
      .sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate))
  ), [upcomingRecurring, reminderSettingsBySeries]);

  const dueRecurringReminders = useMemo(() => {
    const forcedEntries = upcomingRecurring.filter((entry) => forcedDueReminderIds.includes(entry.id));
    const mergedEntriesMap = new Map();
    [...baseDueRecurringReminders, ...forcedEntries].forEach((entry) => {
      mergedEntriesMap.set(entry.id, entry);
    });
    const mergedEntries = Array.from(mergedEntriesMap.values()).sort(
      (a, b) => new Date(a.transactionDate) - new Date(b.transactionDate)
    );
    const nowMs = Date.now();
    return mergedEntries.filter((entry) => {
      const action = reminderActionsByOccurrence[entry.id];
      if (!action) {
        return true;
      }
      if (action.status === 'paid') {
        return false;
      }
      if (action.status === 'snoozed' && action.snoozedUntil) {
        const snoozeUntilMs = new Date(action.snoozedUntil).getTime();
        if (Number.isFinite(snoozeUntilMs) && nowMs < snoozeUntilMs) {
          return false;
        }
      }
      return true;
    });
  }, [baseDueRecurringReminders, forcedDueReminderIds, reminderActionsByOccurrence, upcomingRecurring]);

  const dueReminderSignature = useMemo(() => (
    dueRecurringReminders.map((entry) => entry.id).sort().join('|')
  ), [dueRecurringReminders]);

  const clearReminderUi = useCallback((occurrenceId) => {
    setSnoozeDraftByOccurrence((current) => {
      if (!current[occurrenceId]) {
        return current;
      }
      const next = { ...current };
      delete next[occurrenceId];
      return next;
    });
    setIsReminderMenuOpenByOccurrence((current) => {
      if (!current[occurrenceId]) {
        return current;
      }
      const next = { ...current };
      delete next[occurrenceId];
      return next;
    });
  }, []);

  const submitRecurringPayment = useCallback(async (entry) => {
    const recurringTransactionId = String(entry?.recurringTransactionId || '').trim();
    const occurrenceDate = String(entry?.transactionDate || '').trim();
    if (!recurringTransactionId || !occurrenceDate) {
      throw new Error('Reminder is missing recurring payment details.');
    }

    if (typeof recordRecurringPayment === 'function') {
      try {
        return await recordRecurringPayment(entry);
      } catch (error) {
        if (String(error?.message || '').includes('recordRecurringPayment is not a function')) {
          return postRecurringPaymentDirect(recurringTransactionId, occurrenceDate);
        }
        throw error;
      }
    }

    if (typeof transactionsAPI.recordRecurringPayment === 'function') {
      return transactionsAPI.recordRecurringPayment(recurringTransactionId, occurrenceDate);
    }

    return postRecurringPaymentDirect(recurringTransactionId, occurrenceDate);
  }, [recordRecurringPayment]);

  const markReminderAsPaid = useCallback(async (entry) => {
    const occurrenceId = String(entry?.id || '').trim();
    if (!occurrenceId) {
      return;
    }
    setIsRecordingPaymentByOccurrence((current) => ({
      ...current,
      [occurrenceId]: true,
    }));
    try {
      await submitRecurringPayment(entry);
      setReminderActionsByOccurrence((current) => {
        if (!current[occurrenceId]) {
          return current;
        }
        const next = { ...current };
        delete next[occurrenceId];
        return next;
      });
      clearReminderUi(occurrenceId);
    } finally {
      setIsRecordingPaymentByOccurrence((current) => {
        if (!current[occurrenceId]) {
          return current;
        }
        const next = { ...current };
        delete next[occurrenceId];
        return next;
      });
    }
  }, [clearReminderUi, setReminderActionsByOccurrence, submitRecurringPayment]);

  const startReminderSnooze = useCallback((occurrenceId) => {
    if (!occurrenceId) {
      return;
    }
    setSnoozeDraftByOccurrence((current) => ({
      ...current,
      [occurrenceId]: current[occurrenceId] || buildDefaultSnoozeDraft(),
    }));
  }, []);

  const cancelReminderSnooze = useCallback((occurrenceId) => {
    if (!occurrenceId) {
      return;
    }
    setSnoozeDraftByOccurrence((current) => {
      if (!current[occurrenceId]) {
        return current;
      }
      const next = { ...current };
      delete next[occurrenceId];
      return next;
    });
  }, []);

  const saveReminderSnooze = useCallback((occurrenceId) => {
    const draft = snoozeDraftByOccurrence[occurrenceId];
    const dateValue = String(draft?.date || '').trim();
    const rawTimeValue = String(draft?.time || '').trim() || '09:00';
    if (!dateValue) {
      return;
    }

    const normalizedTimeValue = normalizeTimeToQuarterHour(rawTimeValue);
    const nextDateTime = new Date(`${dateValue}T${normalizedTimeValue}`);
    if (Number.isNaN(nextDateTime.getTime())) {
      return;
    }

    setReminderActionsByOccurrence((current) => ({
      ...current,
      [occurrenceId]: {
        status: 'snoozed',
        snoozedUntil: nextDateTime.toISOString(),
        updatedAt: new Date().toISOString(),
      },
    }));
    cancelReminderSnooze(occurrenceId);
  }, [cancelReminderSnooze, setReminderActionsByOccurrence, snoozeDraftByOccurrence]);

  const triggerDueNowForTesting = useCallback(() => {
    const sourceEntries = visibleUpcomingRecurring.length
      ? visibleUpcomingRecurring
      : thisMonthUpcomingRecurring.length
        ? thisMonthUpcomingRecurring
        : upcomingRecurring;
    const targetIds = sourceEntries.slice(0, 3).map((entry) => entry.id);
    if (!targetIds.length) {
      return;
    }
    setCollapsedSections((current) => ({
      ...current,
      'upcoming-reminders': false,
    }));
    setForcedDueReminderIds((current) => {
      const merged = new Set([...current, ...targetIds]);
      return Array.from(merged);
    });
    setReminderActionsByOccurrence((current) => {
      const next = { ...current };
      targetIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    setSnoozeDraftByOccurrence((current) => {
      const next = { ...current };
      targetIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  }, [setCollapsedSections, setReminderActionsByOccurrence, thisMonthUpcomingRecurring, upcomingRecurring, visibleUpcomingRecurring]);

  const openDatePicker = useCallback((inputElement) => {
    if (!inputElement) {
      return;
    }
    if (typeof inputElement.showPicker === 'function') {
      try {
        inputElement.showPicker();
        return;
      } catch (error) {
        // fallback below
      }
    }
    inputElement.focus();
    inputElement.click();
  }, []);

  useEffect(() => {
    if (!hasInitializedReminderUiRef.current) {
      hasInitializedReminderUiRef.current = true;
      return;
    }
    setSnoozeDraftByOccurrence({});
    setIsReminderMenuOpenByOccurrence({});
    setForcedDueReminderIds([]);
    setIsDueReminderModalOpen(false);
    setDismissedDueReminderSignature('');
  }, [selectedMonth]);

  useEffect(() => {
    if (!dueReminderSignature) {
      setIsDueReminderModalOpen(false);
      setDismissedDueReminderSignature('');
      return;
    }
    if (dismissedDueReminderSignature === dueReminderSignature) {
      return;
    }
    setIsDueReminderModalOpen(true);
  }, [dismissedDueReminderSignature, dueReminderSignature]);

  return (
    <>
      <section
        ref={sectionRef}
        onClick={(event) => handleSectionCardClick('upcoming-reminders', event)}
        className={`relative overflow-hidden border border-white/80 bg-gradient-to-br from-white via-sky-50/85 to-indigo-100/55 shadow-[0_14px_34px_-24px_rgba(14,116,144,0.4)] transition-all duration-200 ${
          isCollapsed ? 'rounded-2xl p-3 md:p-4 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-24px_rgba(14,116,144,0.5)]' : 'rounded-3xl p-4 sm:p-5'
        }`}
      >
        <div className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/90 to-transparent" />
        <div className="relative">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className={`${isCollapsed ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'} font-semibold tracking-tight text-slate-900`}>Upcoming Reminders</h2>
            </div>
            <div className="flex items-center gap-2">
              {ENABLE_REMINDER_TEST_CONTROLS && (
                <button
                  type="button"
                  onClick={triggerDueNowForTesting}
                  className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
                >
                  Trigger due now
                </button>
              )}
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                {visibleUpcomingRecurring.length}
              </span>
              {renderSectionToggle('upcoming-reminders', isCollapsed, 'Upcoming Reminders')}
            </div>
          </div>

          {!isCollapsed && dueRecurringReminders.length > 0 && (
            <div className="mt-2 rounded-xl border border-sky-300 bg-sky-50 px-3 py-2.5" data-prevent-section-toggle="true">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-sky-900">Due reminders (in-app): {dueRecurringReminders.length}</p>
                <button
                  type="button"
                  onClick={() => setIsDueReminderModalOpen(true)}
                  className="rounded-md border border-sky-300 bg-white px-2.5 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-100"
                  data-prevent-section-toggle="true"
                >
                  Open popup
                </button>
              </div>
            </div>
          )}

          {!isCollapsed && (upcomingRecurringLoading ? (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white/80 px-4 py-4 text-sm text-slate-600" data-prevent-section-toggle="true">Loading recurring reminders...</div>
          ) : upcomingRecurringError ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700" data-prevent-section-toggle="true">{upcomingRecurringError}</div>
          ) : (
            <div className="mt-3" data-prevent-section-toggle="true">
              <div className="mb-3 rounded-full border border-slate-200 bg-slate-100 p-1">
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setUpcomingView('week')}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      upcomingView === 'week'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:bg-white/60'
                    }`}
                  >
                    This Week
                  </button>
                  <button
                    type="button"
                    onClick={() => setUpcomingView('month')}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      upcomingView === 'month'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:bg-white/60'
                    }`}
                  >
                    This Month
                  </button>
                </div>
              </div>
              {visibleUpcomingRecurring.length > 0 ? (
                <div className="space-y-2">
                  {visibleUpcomingRecurring.map((entry) => {
                    const seriesId = String(entry?.recurringTransactionId || '').trim();
                    const reminderSetting = reminderSettingsBySeries[seriesId] || {};
                    const selectedReminderDays = getSelectedReminderDays(reminderSetting);
                    const isReminderMenuOpen = Boolean(isReminderMenuOpenByOccurrence[entry.id]);
                    const isRecordingPayment = Boolean(isRecordingPaymentByOccurrence[entry.id]);
                    const remindButtonLabel = selectedReminderDays === null
                      ? 'Remind'
                      : `${selectedReminderDays} day${selectedReminderDays === 1 ? '' : 's'} before`;
                    return (
                      <article key={`upcoming-recurring-${entry.id}`} className="rounded-xl border border-slate-200 bg-slate-50/70 p-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{entry.storeName || 'Recurring transaction'}</p>
                            <p className="text-xs text-slate-600">{formatDate(entry.transactionDate)} - {formatRecurrenceFrequency(entry.recurrenceFrequency)}</p>
                          </div>
                          <p className="text-sm font-semibold text-slate-900">{formatCurrency(entry.itemTotal)}</p>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          <button
                            type="button"
                            onClick={() => {
                              setIsReminderMenuOpenByOccurrence((current) => ({
                                ...current,
                                [entry.id]: !current[entry.id],
                              }));
                            }}
                            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            {remindButtonLabel}
                          </button>
                          <button
                            type="button"
                            onClick={() => markReminderAsPaid(entry)}
                            disabled={isRecordingPayment}
                            className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                          >
                            {isRecordingPayment ? 'Recording...' : 'Paid'}
                          </button>
                          {isReminderMenuOpen && (
                            <select
                              value={selectedReminderDays === null ? '' : String(selectedReminderDays)}
                              onChange={(event) => {
                                const nextValue = String(event.target.value || '').trim();
                                if (!nextValue) {
                                  updateRecurringReminderSetting(seriesId, {
                                    reminderDays: null,
                                    enabled: false,
                                  });
                                } else {
                                  const nextDays = Number(nextValue);
                                  if (!Number.isFinite(nextDays)) {
                                    return;
                                  }
                                  updateRecurringReminderSetting(seriesId, {
                                    reminderDays: nextDays,
                                    daysBefore: nextDays,
                                    enabled: true,
                                  });
                                }
                                setIsReminderMenuOpenByOccurrence((current) => ({
                                  ...current,
                                  [entry.id]: false,
                                }));
                              }}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
                            >
                              <option value="">No reminder selected</option>
                              {REMINDER_DAY_OPTIONS.map((days) => (
                                <option key={`reminder-days-${days}`} value={days}>
                                  {days} day{days === 1 ? '' : 's'} before
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                  {upcomingView === 'week'
                    ? 'No recurring expenses due for the rest of this week.'
                    : 'No upcoming recurring transactions left this month.'}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {isDueReminderModalOpen && dueRecurringReminders.length > 0 && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-900/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-sky-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-sky-100 px-4 py-3">
              <h3 className="text-base font-semibold text-slate-900">Due reminders</h3>
              <button
                type="button"
                onClick={() => {
                  setIsDueReminderModalOpen(false);
                  setDismissedDueReminderSignature(dueReminderSignature);
                }}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                Close
              </button>
            </div>
            <div className="max-h-[70vh] space-y-2 overflow-y-auto px-4 py-3">
              {dueRecurringReminders.map((entry) => {
                const seriesId = String(entry?.recurringTransactionId || '').trim();
                const daysBefore = getSelectedReminderDays(reminderSettingsBySeries[seriesId]) ?? 1;
                const daysLabel = daysBefore === 1 ? '1 day' : `${daysBefore} days`;
                const snoozeDraft = snoozeDraftByOccurrence[entry.id];
                const isRecordingPayment = Boolean(isRecordingPaymentByOccurrence[entry.id]);
                return (
                  <article key={`due-reminder-modal-${entry.id}`} className="rounded-lg border border-sky-200 bg-sky-50/40 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{entry.storeName || 'Transaction'}</p>
                        <p className="text-xs text-slate-600">{formatDate(entry.transactionDate)} ({daysLabel} reminder)</p>
                      </div>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(entry.itemTotal)}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => markReminderAsPaid(entry)}
                        disabled={isRecordingPayment}
                        className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                      >
                        {isRecordingPayment ? 'Recording...' : 'Mark as paid'}
                      </button>
                      <button
                        type="button"
                        onClick={() => startReminderSnooze(entry.id)}
                        className="rounded-md border border-amber-300 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-200"
                      >
                        Snooze
                      </button>
                    </div>
                    {snoozeDraft && (
                      <>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => openDatePicker(snoozeDateInputRefs.current[entry.id])}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openDatePicker(snoozeDateInputRefs.current[entry.id]);
                              }
                            }}
                            className="relative rounded-md border border-slate-300 bg-white px-2 py-1.5 text-left"
                          >
                            <p className="text-xs font-semibold text-slate-700">Date</p>
                            <p className="text-sm text-slate-900">{formatDateForInputDisplay(snoozeDraft.date)}</p>
                            <input
                              ref={(element) => {
                                if (element) {
                                  snoozeDateInputRefs.current[entry.id] = element;
                                } else {
                                  delete snoozeDateInputRefs.current[entry.id];
                                }
                              }}
                              type="date"
                              value={snoozeDraft.date}
                              onChange={(event) => setSnoozeDraftByOccurrence((current) => ({
                                ...current,
                                [entry.id]: {
                                  ...current[entry.id],
                                  date: event.target.value,
                                },
                              }))}
                              className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                              aria-label="Snooze date"
                            />
                          </div>
                          <label className="text-xs text-slate-700">
                            <span className="mb-1 block font-semibold">Time</span>
                            {(() => {
                              const timeParts = getTimePickerParts(snoozeDraft.time);
                              const updateTimePart = (nextPartValues) => {
                                const nextTime = buildTimeFromPickerParts({
                                  hour: nextPartValues.hour ?? timeParts.hour,
                                  minute: nextPartValues.minute ?? timeParts.minute,
                                  period: nextPartValues.period ?? timeParts.period,
                                });
                                setSnoozeDraftByOccurrence((current) => ({
                                  ...current,
                                  [entry.id]: {
                                    ...current[entry.id],
                                    time: nextTime,
                                  },
                                }));
                              };

                              return (
                                <div className="grid grid-cols-3 gap-1.5">
                                  <select
                                    value={timeParts.hour}
                                    onChange={(event) => updateTimePart({ hour: event.target.value })}
                                    className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                                    aria-label="Snooze hour"
                                  >
                                    {Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0')).map((hourOption) => (
                                      <option key={`snooze-hour-${entry.id}-${hourOption}`} value={hourOption}>{hourOption}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={timeParts.minute}
                                    onChange={(event) => updateTimePart({ minute: event.target.value })}
                                    className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                                    aria-label="Snooze minute"
                                  >
                                    {['00', '15', '30', '45'].map((minuteOption) => (
                                      <option key={`snooze-minute-${entry.id}-${minuteOption}`} value={minuteOption}>{minuteOption}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={timeParts.period}
                                    onChange={(event) => updateTimePart({ period: event.target.value })}
                                    className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                                    aria-label="Snooze period"
                                  >
                                    <option value="AM">AM</option>
                                    <option value="PM">PM</option>
                                  </select>
                                </div>
                              );
                            })()}
                          </label>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => saveReminderSnooze(entry.id)}
                            className="rounded-md border border-sky-300 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                          >
                            Save snooze
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelReminderSnooze(entry.id)}
                            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
});

const KeyInsightsSection = memo(function KeyInsightsSection({
  sectionRef,
  isCollapsed,
  handleSectionCardClick,
  renderSectionToggle,
  spendingByCategorySectionRef,
  navigate,
  selectedMonth,
  summary,
  hasPreviousMonthSpent,
  monthOverMonth,
  recurringVsFlexible,
  budgetRingAccent,
  budgetUsageForRing,
  hasConfiguredBudget,
  totalBudget,
  totalSpent,
  budgetDifferenceColor,
  budgetSavingsLabel,
  spendingTrendColor,
  spendingTrendArrow,
  spendingDelta,
  recurringTransactions,
  flexibleTransactions,
  recurringCategorySummary,
  flexibleCategorySummary,
  recurringCategoryTotal,
  flexibleCategoryTotal,
  topCategoryDetail,
  budgetVsActualComparison,
  budgetVsActualTotals,
  transactionsMonthLink,
  formatCurrency,
  formatCurrencyRounded,
  formatSavingsRounded,
  formatDate,
}) {
  const [activeInsight, setActiveInsight] = useState(null);
  const [isInsightDrawerVisible, setIsInsightDrawerVisible] = useState(false);
  const [isInsightSwiping, setIsInsightSwiping] = useState(false);
  const [insightSwipeOffset, setInsightSwipeOffset] = useState(0);
  const [insightExitDirection, setInsightExitDirection] = useState('right');
  const insightCloseTimeoutRef = useRef(null);
  const insightSwipePointerIdRef = useRef(null);
  const insightSwipeStartXRef = useRef(0);
  const insightSwipeStartYRef = useRef(0);
  const insightSwipeIsHorizontalRef = useRef(null);

  const openInsight = useCallback((insightKey) => {
    if (insightCloseTimeoutRef.current) {
      clearTimeout(insightCloseTimeoutRef.current);
      insightCloseTimeoutRef.current = null;
    }
    setInsightExitDirection('right');
    setInsightSwipeOffset(0);
    setIsInsightSwiping(false);
    setActiveInsight(insightKey);
    requestAnimationFrame(() => {
      setIsInsightDrawerVisible(true);
    });
  }, []);

  const closeInsight = useCallback((direction = 'right') => {
    setInsightExitDirection(direction);
    setIsInsightSwiping(false);
    setInsightSwipeOffset(0);
    setIsInsightDrawerVisible(false);
    insightCloseTimeoutRef.current = setTimeout(() => {
      setActiveInsight(null);
      insightCloseTimeoutRef.current = null;
    }, 140);
  }, []);

  const handleInsightPointerDown = useCallback((event) => {
    if (!isInsightDrawerVisible) {
      return;
    }
    if (event.pointerType === 'mouse') {
      return;
    }
    if (event.pointerType === 'pen' && event.button !== 0) {
      return;
    }

    insightSwipePointerIdRef.current = event.pointerId;
    insightSwipeStartXRef.current = event.clientX;
    insightSwipeStartYRef.current = event.clientY;
    insightSwipeIsHorizontalRef.current = null;
    setIsInsightSwiping(true);
    setInsightSwipeOffset(0);
  }, [isInsightDrawerVisible]);

  const handleInsightPointerMove = useCallback((event) => {
    if (!isInsightSwiping || insightSwipePointerIdRef.current !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - insightSwipeStartXRef.current;
    const deltaY = event.clientY - insightSwipeStartYRef.current;
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);

    if (insightSwipeIsHorizontalRef.current === null) {
      if (absoluteX < 8 && absoluteY < 8) {
        return;
      }
      insightSwipeIsHorizontalRef.current = absoluteX > absoluteY * 1.15;
    }

    if (!insightSwipeIsHorizontalRef.current) {
      return;
    }

    setInsightSwipeOffset(deltaX < 0 ? deltaX : 0);
  }, [isInsightSwiping]);

  const handleInsightPointerEnd = useCallback((event) => {
    if (!isInsightSwiping || insightSwipePointerIdRef.current !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - insightSwipeStartXRef.current;
    const shouldCloseFromSwipe = Boolean(insightSwipeIsHorizontalRef.current) && deltaX < -110;
    setIsInsightSwiping(false);
    insightSwipePointerIdRef.current = null;
    insightSwipeIsHorizontalRef.current = null;

    if (shouldCloseFromSwipe) {
      closeInsight('left');
      return;
    }

    setInsightSwipeOffset(0);
  }, [closeInsight, isInsightSwiping]);

  const handleInsightPointerCancel = useCallback((event) => {
    if (!isInsightSwiping || insightSwipePointerIdRef.current !== event.pointerId) {
      return;
    }

    setIsInsightSwiping(false);
    insightSwipePointerIdRef.current = null;
    insightSwipeIsHorizontalRef.current = null;
    setInsightSwipeOffset(0);
  }, [isInsightSwiping]);

  useEffect(() => {
    setActiveInsight(null);
    setIsInsightDrawerVisible(false);
    setInsightSwipeOffset(0);
    setIsInsightSwiping(false);
    setInsightExitDirection('right');
  }, [selectedMonth]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && activeInsight) {
        closeInsight();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [activeInsight, closeInsight]);

  useEffect(() => () => {
    if (insightCloseTimeoutRef.current) {
      clearTimeout(insightCloseTimeoutRef.current);
    }
  }, []);

  return (
    <>
      <section
        ref={sectionRef}
        onClick={(event) => handleSectionCardClick('key-insights', event)}
        className={`group relative overflow-hidden border border-white/80 bg-gradient-to-br from-white via-violet-50/90 to-fuchsia-100/55 backdrop-blur shadow-[0_14px_34px_-24px_rgba(124,58,237,0.45)] transition-all duration-200 ${isCollapsed ? 'rounded-2xl p-3 md:p-4 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-24px_rgba(124,58,237,0.52)]' : 'rounded-3xl p-4 sm:p-5 md:p-6'}`}
      >
        <div className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-violet-300/90 to-transparent" />
        <div className={`${isCollapsed ? 'mb-0' : 'mb-4'} flex items-center justify-between gap-3`}>
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="h-6 w-2 rounded-full bg-gradient-to-b from-violet-500 to-fuchsia-500 shadow-[0_0_0_2px_rgba(255,255,255,0.7)]" aria-hidden="true" />
            <h2 className={`${isCollapsed ? 'text-base sm:text-lg' : 'text-xl'} font-semibold tracking-tight text-slate-900`}>Key Insights</h2>
          </div>
          {renderSectionToggle('key-insights', isCollapsed, 'Key Insights')}
        </div>

        {!isCollapsed && (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            <button
              type="button"
              onClick={() => spendingByCategorySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex flex-col text-left rounded-2xl border border-slate-200/80 bg-white p-2.5 sm:p-3.5 transition-transform duration-100 md:hover:-translate-y-1 md:hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Scroll to Spending by Category section"
            >
              <div className="-mt-0.5 flex items-center justify-between gap-2">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Total Spent</p>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                  <CurrencyDollarIcon className="h-5 w-5" aria-hidden="true" />
                </span>
              </div>
              <p className="mt-0.5 text-xl sm:text-2xl font-semibold leading-tight text-slate-900">{formatCurrencyRounded(summary.totalSpent)}</p>
              <div className="mt-2.5 space-y-2.5">
                <p className="text-xs sm:text-sm leading-snug text-slate-500">
                  Last month: {hasPreviousMonthSpent ? formatCurrencyRounded(monthOverMonth.previousTotalSpent || 0) : 'No data'}
                </p>
                <div className="space-y-0.5">
                  <p className="text-[10px] sm:text-[11px] font-semibold tracking-wide text-slate-500">
                    Spent most on:
                  </p>
                  <p className="min-w-0 truncate text-xs sm:text-sm text-slate-700">
                    {summary.topCategory
                      ? `${summary.topCategory.name || 'Uncategorized'} - ${formatCurrencyRounded(summary.topCategory.value)}`
                      : '--'}
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => openInsight('spending-split')}
              className="text-left rounded-2xl border border-indigo-100/80 bg-gradient-to-br from-white via-indigo-50/70 to-cyan-50 p-2.5 sm:p-3.5 transition-transform duration-100 md:hover:-translate-y-1 md:hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Open recurring vs flexible spending details"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Spending Split</p>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-indigo-200/80 bg-white/80 text-indigo-600">
                  <TvIcon className="h-5 w-5" aria-hidden="true" />
                </span>
              </div>
              <p className="mt-1 text-lg sm:text-xl font-semibold text-slate-900">
                {Math.round(Number(recurringVsFlexible.flexiblePercentage || 0)) >= Math.round(Number(recurringVsFlexible.recurringPercentage || 0))
                  ? `${Math.round(Number(recurringVsFlexible.flexiblePercentage || 0))}% Flexible`
                  : `${Math.round(Number(recurringVsFlexible.recurringPercentage || 0))}% Recurring`}
              </p>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200/90">
                <div className="flex h-full w-full">
                  <div
                    className="h-full bg-indigo-500"
                    style={{ width: `${Math.max(0, Math.min(100, Number(recurringVsFlexible.recurringPercentage || 0)))}%` }}
                  />
                  <div
                    className="h-full bg-cyan-500"
                    style={{ width: `${Math.max(0, Math.min(100, Number(recurringVsFlexible.flexiblePercentage || 0)))}%` }}
                  />
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] sm:text-xs">
                <div className="rounded-lg border border-indigo-100 bg-white/85 px-2 py-1">
                  <p className="text-indigo-700 font-semibold">Recurring</p>
                  <p className="text-slate-700 font-semibold">{formatCurrencyRounded(recurringVsFlexible.recurringTotal)}</p>
                </div>
                <div className="rounded-lg border border-cyan-100 bg-white/85 px-2 py-1">
                  <p className="text-cyan-700 font-semibold">Flexible</p>
                  <p className="text-slate-700 font-semibold">{formatCurrencyRounded(recurringVsFlexible.flexibleTotal)}</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => openInsight('budget-vs-actual')}
              className="text-left rounded-2xl border border-slate-200/80 bg-white p-2.5 sm:p-3.5 transition-transform duration-100 md:hover:-translate-y-1 md:hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Open Budget vs Actual details"
            >
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Budget vs Actual</p>
              <div className="mt-2 flex items-center gap-2.5 sm:gap-3">
                <div
                  className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full p-[3px] sm:h-14 sm:w-14"
                  style={{
                    background: `conic-gradient(${hexToRgba(budgetRingAccent, 0.55)} ${budgetUsageForRing}%, #e2e8f0 ${budgetUsageForRing}% 100%)`,
                  }}
                >
                  <div className="flex h-full w-full items-center justify-center rounded-full bg-white text-[9px] font-semibold text-slate-700 sm:text-[10px]">
                    {hasConfiguredBudget ? `${Math.round(budgetUsageForRing)}%` : 'Set'}
                  </div>
                </div>
                <div className="min-w-0 space-y-0.5 text-[11px] sm:text-xs">
                  <p className="truncate text-slate-600">B: {formatCurrencyRounded(totalBudget)}</p>
                  <p className="truncate text-slate-600">S: {formatCurrencyRounded(totalSpent)}</p>
                  <p className={`truncate font-semibold ${budgetDifferenceColor}`}>Sav..: {budgetSavingsLabel}</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate(`/dashboard/category-comparison?month=${selectedMonth}`)}
              className="text-left rounded-2xl border border-slate-200/80 bg-white p-2.5 sm:p-3.5 transition-transform duration-100 md:hover:-translate-y-1 md:hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Open full Month over Month comparison"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Month over Month</p>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                  <ArrowsRightLeftIcon className="h-5 w-5" aria-hidden="true" />
                </span>
              </div>
              <p className={`text-xl sm:text-2xl font-semibold mt-1 ${spendingTrendColor}`}>
                {hasPreviousMonthSpent
                  ? `${spendingTrendArrow} ${formatCurrencyRounded(Math.abs(spendingDelta))}`
                  : '--'}
              </p>
              <p className="text-sm sm:text-base text-slate-700 mt-1">
                {monthOverMonth.hasPreviousMonthData && monthOverMonth.changePercentage !== null
                  ? `${Number(monthOverMonth.changePercentage) >= 0 ? '+' : ''}${Math.round(Number(monthOverMonth.changePercentage))}%`
                  : 'No prior month data'}
              </p>
            </button>
          </div>
        )}
      </section>

      {activeInsight && (
        <div className="fixed inset-0 z-50">
          <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-900/35 via-slate-800/30 to-blue-900/30 backdrop-blur-[2px] transition-opacity duration-150 ${isInsightDrawerVisible ? 'opacity-100' : 'opacity-0'}`} />
          <button
            type="button"
            className={`absolute inset-0 transition-opacity duration-150 ${isInsightDrawerVisible ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeInsight}
            aria-label="Close insight details"
          />
          <aside
            className={`absolute bottom-4 left-[10%] right-0 top-0 overflow-y-auto rounded-2xl bg-gradient-to-b from-white to-slate-50/95 shadow-2xl border border-slate-200/90 p-5 md:bottom-4 md:left-[10%] md:right-4 md:top-4 md:p-6 ${isInsightSwiping ? '' : 'transition-transform duration-150 ease-out'}`}
            style={{
              transform: isInsightDrawerVisible
                ? `translateX(${insightSwipeOffset}px)`
                : insightExitDirection === 'left'
                  ? 'translateX(-100%)'
                  : 'translateX(100%)',
              touchAction: 'pan-y',
            }}
            onPointerDown={handleInsightPointerDown}
            onPointerMove={handleInsightPointerMove}
            onPointerUp={handleInsightPointerEnd}
            onPointerCancel={handleInsightPointerCancel}
          >
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  {activeInsight === 'top-category' && 'Top Category Details'}
                  {activeInsight === 'budget-vs-actual' && 'Budget vs Actual'}
                  {activeInsight === 'spending-split' && 'Recurring vs Flexible Transactions'}
                </h3>
                <p className="text-sm text-slate-500 mt-1">{formatMonthLabel(selectedMonth)}</p>
              </div>
              <button
                type="button"
                onClick={closeInsight}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close details panel"
              >
                &times;
              </button>
            </div>

            {activeInsight === 'spending-split' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-indigo-200/90 bg-gradient-to-b from-indigo-50 to-white p-4 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Recurring</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{formatCurrencyRounded(recurringVsFlexible.recurringTotal)}</p>
                    <p className="text-xs text-slate-600">{recurringTransactions.length} transactions</p>
                  </div>
                  <div className="rounded-2xl border border-cyan-200/90 bg-gradient-to-b from-cyan-50 to-white p-4 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-cyan-700">Flexible</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{formatCurrencyRounded(recurringVsFlexible.flexibleTotal)}</p>
                    <p className="text-xs text-slate-600">{flexibleTransactions.length} transactions</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-slate-900">Recurring Categories</h4>
                      <p className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                        Total: {formatCurrency(recurringCategoryTotal)}
                      </p>
                    </div>
                    {recurringCategorySummary.length > 0 ? (
                      <>
                        <div className="sm:hidden divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
                          {recurringCategorySummary.map((entry) => (
                            <article key={`recurring-category-summary-mobile-${toComparableText(entry.category)}`} className="px-3 py-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900 break-words">{entry.category}</p>
                                  <p className="text-[11px] text-slate-600">{formatDate(entry.latestTransactionDate)} • {entry.latestStoreName}</p>
                                </div>
                                <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{formatCurrency(entry.totalAmount)}</p>
                              </div>
                            </article>
                          ))}
                        </div>

                        <div className="hidden sm:block overflow-x-auto">
                          <table className="w-full min-w-[620px]">
                            <thead>
                              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                                <th className="py-2 pr-3 font-semibold">Transaction Date</th>
                                <th className="py-2 px-3 font-semibold">Category</th>
                                <th className="py-2 px-3 font-semibold">Store Name</th>
                                <th className="py-2 pl-3 text-right font-semibold">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recurringCategorySummary.map((entry) => (
                                <tr key={`recurring-category-summary-${toComparableText(entry.category)}`} className="border-b border-slate-100 bg-white even:bg-slate-50/40">
                                  <td className="py-2 pr-3 text-sm text-slate-700">{formatDate(entry.latestTransactionDate)}</td>
                                  <td className="py-2 px-3 text-sm font-medium text-slate-900">{entry.category}</td>
                                  <td className="py-2 px-3 text-sm text-slate-700">{entry.latestStoreName}</td>
                                  <td className="py-2 pl-3 text-right text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(entry.totalAmount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">No recurring transactions found for this month.</p>
                    )}
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-slate-900">Flexible Categories</h4>
                      <p className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700">
                        Total: {formatCurrency(flexibleCategoryTotal)}
                      </p>
                    </div>
                    {flexibleCategorySummary.length > 0 ? (
                      <>
                        <div className="sm:hidden divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
                          {flexibleCategorySummary.map((entry) => (
                            <article key={`flexible-category-summary-mobile-${toComparableText(entry.category)}`} className="px-3 py-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900 break-words">{entry.category}</p>
                                  <p className="text-[11px] text-slate-600">{formatDate(entry.latestTransactionDate)} • {entry.latestStoreName}</p>
                                </div>
                                <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{formatCurrency(entry.totalAmount)}</p>
                              </div>
                            </article>
                          ))}
                        </div>

                        <div className="hidden sm:block overflow-x-auto">
                          <table className="w-full min-w-[620px]">
                            <thead>
                              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                                <th className="py-2 pr-3 font-semibold">Transaction Date</th>
                                <th className="py-2 px-3 font-semibold">Category</th>
                                <th className="py-2 px-3 font-semibold">Store Name</th>
                                <th className="py-2 pl-3 text-right font-semibold">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {flexibleCategorySummary.map((entry) => (
                                <tr key={`flexible-category-summary-${toComparableText(entry.category)}`} className="border-b border-slate-100 bg-white even:bg-slate-50/40">
                                  <td className="py-2 pr-3 text-sm text-slate-700">{formatDate(entry.latestTransactionDate)}</td>
                                  <td className="py-2 px-3 text-sm font-medium text-slate-900">{entry.category}</td>
                                  <td className="py-2 px-3 text-sm text-slate-700">{entry.latestStoreName}</td>
                                  <td className="py-2 pl-3 text-right text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(entry.totalAmount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">No flexible transactions found for this month.</p>
                    )}
                  </section>
                </div>
              </div>
            )}

            {activeInsight === 'top-category' && (
              <div className="space-y-4">
                {topCategoryDetail ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 p-4">
                      <p className="text-sm text-slate-500">Category</p>
                      <p className="text-2xl font-semibold text-slate-900 mt-1">{topCategoryDetail.name}</p>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <p className="text-slate-600">Total: <span className="font-semibold text-slate-900">{formatCurrency(topCategoryDetail.value)}</span></p>
                        <p className="text-slate-600">Share: <span className="font-semibold text-slate-900">{Number(topCategoryDetail.percentage || 0).toFixed(1)}%</span></p>
                        <p className="text-slate-600">Transactions: <span className="font-semibold text-slate-900">{topCategoryDetail.count}</span></p>
                        <p className="text-slate-600">Avg Txn: <span className="font-semibold text-slate-900">{formatCurrency(topCategoryDetail.averageTransaction)}</span></p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 p-4">
                      <p className="text-sm font-medium text-slate-900 mb-3">Top Merchants</p>
                      {topCategoryDetail.topMerchants?.length ? (
                        <div className="space-y-2">
                          {topCategoryDetail.topMerchants.map((merchant) => (
                            <div key={`merchant-${merchant.name}`} className="flex items-center justify-between gap-3">
                              <p className="text-sm text-slate-700 truncate">{merchant.name}</p>
                              <p className="text-sm font-semibold text-slate-900">{formatCurrency(merchant.value)}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">No merchant breakdown available.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">No top category data for this month.</p>
                )}
              </div>
            )}

            {activeInsight === 'budget-vs-actual' && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-full p-[5px]"
                      style={{
                        background: `conic-gradient(${hexToRgba(budgetRingAccent, 0.55)} ${budgetUsageForRing}%, #e2e8f0 ${budgetUsageForRing}% 100%)`,
                      }}
                    >
                      <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-white">
                        <span className="text-lg font-semibold text-slate-900">
                          {hasConfiguredBudget ? `${Math.round(budgetUsageForRing)}%` : '--'}
                        </span>
                        <span className="text-[10px] text-slate-500">used</span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700">Monthly budget overview</p>
                      <p className={`mt-1 text-sm font-semibold ${budgetDifferenceColor}`}>
                        {hasConfiguredBudget
                          ? totalBudget - totalSpent >= 0
                            ? 'Within budget'
                            : 'Over budget'
                          : 'No budget set'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3 text-sm">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Budget</p>
                      <p className="mt-1 font-semibold text-slate-900">{formatCurrency(totalBudget)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Spent</p>
                      <p className="mt-1 font-semibold text-slate-900">{formatCurrency(totalSpent)}</p>
                    </div>
                    <div className={`rounded-lg border p-2.5 ${
                      hasConfiguredBudget
                        ? totalBudget - totalSpent >= 0
                          ? 'border-emerald-200 bg-emerald-50'
                          : 'border-rose-200 bg-rose-50'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                    >
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Savings</p>
                      <p className={`mt-1 font-semibold ${budgetDifferenceColor}`}>
                        {hasConfiguredBudget ? formatSavingsRounded(totalBudget - totalSpent) : '--'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="mb-3 text-sm font-medium text-slate-900">By category</p>
                  {budgetVsActualComparison.length > 0 ? (
                    <>
                      <div className="sm:hidden overflow-hidden rounded-xl border border-slate-200 bg-white">
                        <table className="w-full table-fixed">
                          <colgroup>
                            <col className="w-[38%]" />
                            <col className="w-[20%]" />
                            <col className="w-[20%]" />
                            <col className="w-[22%]" />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              <th className="px-2 py-2 text-left">Category</th>
                              <th className="px-1.5 py-2 text-right">Budget</th>
                              <th className="px-1.5 py-2 text-right">Actual</th>
                              <th className="px-2 py-2 text-right">Savings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {budgetVsActualComparison.map((entry) => {
                              const difference = Number(entry.difference || 0);
                              const differenceColor = difference >= 0 ? 'text-emerald-600' : 'text-rose-600';
                              const savingsLabel = formatSavingsRounded(difference);
                              return (
                                <tr key={`budget-vs-actual-mobile-${entry.id}`} className="border-b border-slate-100 text-sm">
                                  <td className="px-2 py-2 align-top text-[12px] font-medium text-slate-900 break-words leading-snug">
                                    {renderCategoryLabel(entry.name)}
                                  </td>
                                  <td className="px-1.5 py-2 text-right whitespace-nowrap text-[12px] text-slate-700 tabular-nums">
                                    {formatCurrencyRounded(entry.budget)}
                                  </td>
                                  <td className="px-1.5 py-2 text-right whitespace-nowrap text-[12px] text-slate-700 tabular-nums">
                                    {formatCurrencyRounded(entry.actual)}
                                  </td>
                                  <td className={`px-2 py-2 text-right whitespace-nowrap text-[12px] font-semibold tabular-nums ${differenceColor}`}>
                                    {savingsLabel}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-slate-300 bg-slate-50 text-[12px] font-semibold">
                              <td className="px-2 py-2 text-slate-900">Total</td>
                              <td className="px-1.5 py-2 text-right whitespace-nowrap text-slate-800 tabular-nums">
                                {formatCurrencyRounded(budgetVsActualTotals.budget)}
                              </td>
                              <td className="px-1.5 py-2 text-right whitespace-nowrap text-slate-800 tabular-nums">
                                {formatCurrencyRounded(budgetVsActualTotals.actual)}
                              </td>
                              <td className={`px-2 py-2 text-right whitespace-nowrap tabular-nums ${
                                budgetVsActualTotals.savings >= 0 ? 'text-emerald-600' : 'text-rose-600'
                              }`}
                              >
                                {formatSavingsRounded(budgetVsActualTotals.savings)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full min-w-[680px]">
                          <thead>
                            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                              <th className="py-3 pr-3 font-semibold">Category</th>
                              <th className="py-3 px-3 font-semibold text-right">Budgeted</th>
                              <th className="py-3 px-3 font-semibold text-right">Actual</th>
                              <th className="py-3 pl-3 font-semibold text-right">Savings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {budgetVsActualComparison.map((entry) => {
                              const difference = Number(entry.difference || 0);
                              const differenceColor = difference >= 0 ? 'text-emerald-600' : 'text-rose-600';
                              const savingsLabel = formatSavingsRounded(difference);
                              return (
                                <tr key={`budget-vs-actual-${entry.id}`} className="border-b border-slate-100">
                                  <td className="py-3 pr-3 font-medium text-slate-900 leading-snug">
                                    {renderCategoryLabel(entry.name)}
                                  </td>
                                  <td className="py-3 px-3 text-right text-slate-800 tabular-nums">
                                    {formatCurrencyRounded(entry.budget)}
                                  </td>
                                  <td className="py-3 px-3 text-right text-slate-700 tabular-nums">
                                    {formatCurrencyRounded(entry.actual)}
                                  </td>
                                  <td className={`py-3 pl-3 text-right font-semibold tabular-nums ${differenceColor}`}>
                                    {savingsLabel}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                              <td className="py-3 pr-3 text-slate-900">Total</td>
                              <td className="py-3 px-3 text-right text-slate-900 tabular-nums">
                                {formatCurrencyRounded(budgetVsActualTotals.budget)}
                              </td>
                              <td className="py-3 px-3 text-right text-slate-900 tabular-nums">
                                {formatCurrencyRounded(budgetVsActualTotals.actual)}
                              </td>
                              <td className={`py-3 pl-3 text-right tabular-nums ${
                                budgetVsActualTotals.savings >= 0 ? 'text-emerald-600' : 'text-rose-600'
                              }`}
                              >
                                {formatSavingsRounded(budgetVsActualTotals.savings)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">No budget vs actual category data for this month.</p>
                  )}
                </div>
              </div>
            )}

            <div className="sticky bottom-0 mt-6 -mx-5 border-t border-slate-200 bg-white/95 backdrop-blur px-5 py-4 md:-mx-6 md:px-6">
              <Link
                to={transactionsMonthLink}
                className="inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
              >
                View transactions
              </Link>
            </div>
          </aside>
        </div>
      )}
    </>
  );
});

function Dashboard() {
  const [persistedDashboardUiState, setPersistedDashboardUiState] = usePersistentUiState(
    DASHBOARD_UI_STATE_KEY,
    buildDefaultDashboardUiState,
    { storage: 'local', validate: sanitizeDashboardUiState }
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const monthFromQuery = new URLSearchParams(location.search).get('month');
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthFromQuery || '')) {
      return monthFromQuery;
    }
    return persistedDashboardUiState.selectedMonth;
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState(() => persistedDashboardUiState.collapsedSections);
  const [upcomingRecurring, setUpcomingRecurring] = useState([]);
  const [upcomingRecurringLoading, setUpcomingRecurringLoading] = useState(true);
  const [upcomingRecurringError, setUpcomingRecurringError] = useState('');
  const [reminderSettingsBySeries, setReminderSettingsBySeries] = useState(() => (
    readReminderStorageState(RECURRING_REMINDER_STORAGE_KEY)
  ));
  const [reminderActionsByOccurrence, setReminderActionsByOccurrence] = useState(() => (
    readReminderStorageState(RECURRING_REMINDER_ACTIONS_STORAGE_KEY)
  ));
  const [upcomingView, setUpcomingView] = useState(() => persistedDashboardUiState.upcomingView);
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth < 640;
  });

  const assistantSectionRef = useRef(null);
  const upcomingRemindersSectionRef = useRef(null);
  const keyInsightsSectionRef = useRef(null);
  const spendingByCategorySectionRef = useRef(null);
  const monthOverMonthSectionRef = useRef(null);
  const recentTransactionsSectionRef = useRef(null);
  const monthPickerRef = useRef(null);
  const dashboardFetchRequestIdRef = useRef(0);
  const upcomingRecurringFetchRequestIdRef = useRef(0);
  const hasLoadedUpcomingRecurringRef = useRef(false);
  const lastHandledFocusSearchRef = useRef('');

  const [selectedYear, selectedMonthNumber] = useMemo(() => {
    return selectedMonth.split('-').map(Number);
  }, [selectedMonth]);
  const transactionsMonthLink = `/transactions?month=${selectedMonth}&from=dashboard`;

  const areAllSectionsCollapsed = useMemo(() => {
    return COLLAPSIBLE_SECTION_IDS.every((sectionId) => collapsedSections[sectionId]);
  }, [collapsedSections]);
  const hasAnySectionCollapsed = useMemo(() => {
    return COLLAPSIBLE_SECTION_IDS.some((sectionId) => collapsedSections[sectionId]);
  }, [collapsedSections]);
  const isCondensedCollapsedView = hasAnySectionCollapsed;

  const isUpcomingRemindersCollapsed = collapsedSections['upcoming-reminders'];
  const isAssistantCollapsed = collapsedSections.assistant;
  const isKeyInsightsCollapsed = collapsedSections['key-insights'];
  const isSpendingByCategoryCollapsed = collapsedSections['spending-by-category'];
  const isMonthOverMonthCollapsed = collapsedSections['month-over-month'];
  const isRecentTransactionsCollapsed = collapsedSections['recent-transactions'];

  const toggleSectionCollapsed = useCallback((sectionId) => {
    setCollapsedSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  }, []);

  const toggleAllSectionsCollapsed = useCallback(() => {
    setCollapsedSections((current) => {
      const collapseAll = !COLLAPSIBLE_SECTION_IDS.every((sectionId) => current[sectionId]);
      return COLLAPSIBLE_SECTION_IDS.reduce((accumulator, sectionId) => ({
        ...accumulator,
        [sectionId]: collapseAll,
      }), current);
    });
  }, []);

  const handleSectionCardClick = useCallback((sectionId, event) => {
    if (event.target.closest('button, a, input, textarea, select, [role="button"], [data-prevent-section-toggle="true"]')) {
      return;
    }
    toggleSectionCollapsed(sectionId);
  }, [toggleSectionCollapsed]);

  const renderSectionToggle = useCallback((sectionId, isCollapsed, sectionName) => (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        toggleSectionCollapsed(sectionId);
      }}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-all duration-200 ${
        isCollapsed
          ? 'border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-pink-50 text-fuchsia-700 shadow-sm hover:from-fuchsia-100 hover:to-pink-100'
          : 'border-emerald-200 bg-gradient-to-r from-emerald-50 to-cyan-50 text-emerald-700 shadow-sm hover:from-emerald-100 hover:to-cyan-100'
      }`}
      aria-expanded={!isCollapsed}
      aria-label={isCollapsed ? `Expand ${sectionName} section` : `Collapse ${sectionName} section`}
      title={isCollapsed ? 'Expand section' : 'Collapse section'}
      data-prevent-section-toggle="true"
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        className={`h-3.5 w-3.5 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`}
        aria-hidden="true"
      >
        <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-[11px] font-semibold">{isCollapsed ? 'Show' : 'Hide'}</span>
    </button>
  ), [toggleSectionCollapsed]);

  const changeMonthBy = useCallback((delta) => {
    setSelectedMonth((current) => shiftMonthValue(current, delta));
  }, []);

  const openMonthPicker = useCallback(() => {
    setPickerYear(selectedYear);
    setIsMonthPickerOpen(true);
  }, [selectedYear]);

  const handleMonthGridSelect = useCallback((monthNumber) => {
    setSelectedMonth(`${pickerYear}-${String(monthNumber).padStart(2, '0')}`);
    setIsMonthPickerOpen(false);
  }, [pickerYear]);

  const fetchDashboardData = useCallback(async () => {
    const requestId = dashboardFetchRequestIdRef.current + 1;
    dashboardFetchRequestIdRef.current = requestId;

    const applyDashboardData = (nextData) => {
      if (dashboardFetchRequestIdRef.current !== requestId) {
        return;
      }
      setDashboardData(nextData);
    };

    try {
      setLoading(true);
      setError('');
      const data = await dashboardAPI.get(selectedMonthNumber, selectedYear, {
        staleWhileRevalidate: true,
        onBackgroundUpdate: applyDashboardData,
      });
      applyDashboardData(data);
    } catch (err) {
      if (dashboardFetchRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
      setDashboardData(null);
    } finally {
      if (dashboardFetchRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [selectedMonthNumber, selectedYear]);

  const fetchUpcomingRecurringData = useCallback(async () => {
    const requestId = upcomingRecurringFetchRequestIdRef.current + 1;
    upcomingRecurringFetchRequestIdRef.current = requestId;

    const applyUpcomingRecurringData = (nextData) => {
      if (upcomingRecurringFetchRequestIdRef.current !== requestId) {
        return;
      }
      setUpcomingRecurring(normalizeUpcomingRecurringEntries(nextData));
    };

    try {
      setUpcomingRecurringLoading((current) => current || !hasLoadedUpcomingRecurringRef.current);
      setUpcomingRecurringError('');
      const upcomingData = await transactionsAPI.getUpcomingRecurring({
        staleWhileRevalidate: true,
        onBackgroundUpdate: applyUpcomingRecurringData,
      });
      hasLoadedUpcomingRecurringRef.current = true;
      applyUpcomingRecurringData(upcomingData);
    } catch (fetchError) {
      if (upcomingRecurringFetchRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error loading upcoming recurring transactions:', fetchError);
      setUpcomingRecurring([]);
      setUpcomingRecurringError('Failed to load recurring calendar');
    } finally {
      if (upcomingRecurringFetchRequestIdRef.current === requestId) {
        setUpcomingRecurringLoading(false);
      }
    }
  }, []);

  const recordRecurringPayment = useCallback(async (entry) => {
    const recurringTransactionId = String(entry?.recurringTransactionId || '').trim();
    const occurrenceDate = String(entry?.transactionDate || '').trim();
    if (!recurringTransactionId || !occurrenceDate) {
      throw new Error('Reminder is missing recurring payment details.');
    }

    try {
      setError('');
      if (typeof transactionsAPI.recordRecurringPayment === 'function') {
        try {
          await transactionsAPI.recordRecurringPayment(recurringTransactionId, occurrenceDate);
        } catch (paymentError) {
          if (String(paymentError?.message || '').includes('recordRecurringPayment is not a function')) {
            await postRecurringPaymentDirect(recurringTransactionId, occurrenceDate);
          } else {
            throw paymentError;
          }
        }
      } else {
        await postRecurringPaymentDirect(recurringTransactionId, occurrenceDate);
      }
      await Promise.all([
        fetchDashboardData(),
        fetchUpcomingRecurringData(),
      ]);
    } catch (paymentError) {
      console.error('Error recording recurring payment:', paymentError);
      const message = paymentError?.response?.data?.error || paymentError?.message || 'Failed to record recurring payment';
      setError(message);
      throw paymentError;
    }
  }, [fetchDashboardData, fetchUpcomingRecurringData]);

  const openAssistantPage = useCallback((queryText = '') => {
    const params = new URLSearchParams({ month: selectedMonth });
    const trimmedQuery = String(queryText || '').trim();
    if (trimmedQuery) {
      params.set('q', trimmedQuery);
    }
    navigate(`/dashboard/assistant?${params.toString()}`);
  }, [navigate, selectedMonth]);

  const handleAssistantLauncherSubmit = useCallback((event) => {
    event.preventDefault();
    openAssistantPage(aiSearchQuery);
  }, [aiSearchQuery, openAssistantPage]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchUpcomingRecurringData();
  }, [fetchUpcomingRecurringData]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(RECURRING_REMINDER_STORAGE_KEY, JSON.stringify(reminderSettingsBySeries));
    } catch (storageError) {
      console.error('Failed to persist recurring reminder settings:', storageError);
    }
  }, [reminderSettingsBySeries]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(RECURRING_REMINDER_ACTIONS_STORAGE_KEY, JSON.stringify(reminderActionsByOccurrence));
    } catch (storageError) {
      console.error('Failed to persist recurring reminder actions:', storageError);
    }
  }, [reminderActionsByOccurrence]);

  useEffect(() => {
    const monthFromQuery = new URLSearchParams(location.search).get('month');
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthFromQuery || '') && monthFromQuery !== selectedMonth) {
      setSelectedMonth(monthFromQuery);
    }
  }, [location.search, selectedMonth]);

  useEffect(() => {
    setPersistedDashboardUiState({
      selectedMonth,
      collapsedSections,
      upcomingView,
    });
  }, [collapsedSections, selectedMonth, setPersistedDashboardUiState, upcomingView]);

  useEffect(() => {
    const search = String(location.search || '');
    const params = new URLSearchParams(search);
    const focus = String(params.get('focus') || '').trim();
    if (!focus) {
      lastHandledFocusSearchRef.current = '';
      return;
    }
    if (lastHandledFocusSearchRef.current === search) {
      return;
    }

    const allowedFocuses = new Set([
      'upcoming-reminders',
      'assistant',
      'key-insights',
      'spending-by-category',
      'month-over-month',
      'recent-transactions',
    ]);
    if (!allowedFocuses.has(focus)) {
      return;
    }

    setCollapsedSections((current) => ({
      ...current,
      [focus]: false,
    }));

    const refBySection = {
      'upcoming-reminders': upcomingRemindersSectionRef,
      assistant: assistantSectionRef,
      'key-insights': keyInsightsSectionRef,
      'spending-by-category': spendingByCategorySectionRef,
      'month-over-month': monthOverMonthSectionRef,
      'recent-transactions': recentTransactionsSectionRef,
    };
    const targetRef = refBySection[focus];
    const targetElement = targetRef?.current;
    if (targetElement && typeof targetElement.scrollIntoView === 'function') {
      window.setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }

    lastHandledFocusSearchRef.current = search;
  }, [location.search]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMonthPickerOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!isMonthPickerOpen) {
      return undefined;
    }

    const handleOutsidePress = (event) => {
      const pickerNode = monthPickerRef.current;
      if (!pickerNode) {
        return;
      }
      if (!pickerNode.contains(event.target)) {
        setIsMonthPickerOpen(false);
      }
    };

    window.addEventListener('mousedown', handleOutsidePress);
    window.addEventListener('touchstart', handleOutsidePress);
    return () => {
      window.removeEventListener('mousedown', handleOutsidePress);
      window.removeEventListener('touchstart', handleOutsidePress);
    };
  }, [isMonthPickerOpen]);

  useEffect(() => {
    const onResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const summary = dashboardData?.summary || {
    totalSpent: 0,
    transactionCount: 0,
    avgTransaction: 0,
    totalTax: 0,
    topCategory: null,
  };
  const insights = dashboardData?.insights || {};

  const categoryData = useMemo(() => {
    const categories = Array.isArray(dashboardData?.spendingByParentCategory)
      ? dashboardData.spendingByParentCategory
      : [];
    return [...categories].sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  }, [dashboardData]);

  const recentTransactions = useMemo(() => {
    return Array.isArray(dashboardData?.recentTransactions)
      ? dashboardData.recentTransactions
      : [];
  }, [dashboardData]);
  const spendingSplitTransactions = useMemo(() => (
    recentTransactions.filter((transaction) => {
      const transactionDate = parseTransactionDate(transaction?.transactionDate);
      if (!transactionDate) {
        return false;
      }

      return (
        transactionDate.getFullYear() === selectedYear
        && transactionDate.getMonth() + 1 === selectedMonthNumber
      );
    })
  ), [recentTransactions, selectedMonthNumber, selectedYear]);
  const recurringTransactions = useMemo(() => (
    spendingSplitTransactions.filter((transaction) => Boolean(transaction?.recurringTransactionId))
  ), [spendingSplitTransactions]);
  const flexibleTransactions = useMemo(() => (
    spendingSplitTransactions.filter((transaction) => !transaction?.recurringTransactionId)
  ), [spendingSplitTransactions]);
  const recurringCategorySummary = useMemo(() => (
    buildCategorySummary(recurringTransactions)
  ), [recurringTransactions]);
  const flexibleCategorySummary = useMemo(() => (
    buildCategorySummary(flexibleTransactions)
  ), [flexibleTransactions]);
  const recurringCategoryTotal = useMemo(() => (
    recurringCategorySummary.reduce((sum, entry) => sum + toNumber(entry?.totalAmount), 0)
  ), [recurringCategorySummary]);
  const flexibleCategoryTotal = useMemo(() => (
    flexibleCategorySummary.reduce((sum, entry) => sum + toNumber(entry?.totalAmount), 0)
  ), [flexibleCategorySummary]);
  const totalBudget = Number(insights.totalBudget || 0);
  const spentVsBudgetPercentage = totalBudget > 0
    ? Math.min((Number(summary.totalSpent || 0) / totalBudget) * 100, 100)
    : 0;
  const monthOverMonth = insights.monthOverMonth || {
    changeAmount: 0,
    changePercentage: null,
    hasPreviousMonthData: false,
  };
  const recurringVsFlexible = insights.recurringVsFlexible || {
    recurringTotal: 0,
    flexibleTotal: 0,
    recurringPercentage: 0,
    flexiblePercentage: 0,
  };
  const insightDetails = insights.details || {};
  const topCategoryDetail = insightDetails.topCategory || null;
  const totalSpent = Number(summary.totalSpent || 0);
  const budgetDifference = totalBudget - totalSpent;
  const hasConfiguredBudget = totalBudget > 0;
  const budgetSavingsLabel = hasConfiguredBudget
    ? formatSavingsRounded(budgetDifference)
    : '--';
  const budgetDifferenceColor = !hasConfiguredBudget
    ? 'text-slate-500'
    : budgetDifference >= 0
      ? 'text-emerald-600'
      : 'text-rose-600';
  const budgetUsagePercentage = Number(insights.budgetUsagePercentage || spentVsBudgetPercentage || 0);
  const budgetUsageForRing = hasConfiguredBudget
    ? Math.max(0, Math.min(budgetUsagePercentage, 100))
    : 0;
  const budgetRingAccent = !hasConfiguredBudget
    ? '#94a3b8'
    : budgetDifference < 0
      ? '#ef4444'
      : budgetUsageForRing >= 80
        ? '#f59e0b'
        : '#2563eb';
  const spendingDelta = Number(monthOverMonth.changeAmount || 0);
  const hasPreviousMonthSpent = Boolean(monthOverMonth.hasPreviousMonthData);
  const spendingTrendDirection = spendingDelta > 0 ? 'up' : spendingDelta < 0 ? 'down' : 'flat';
  const spendingTrendArrow = spendingTrendDirection === 'up'
    ? '↗'
    : spendingTrendDirection === 'down'
      ? '↘'
      : '→';
  const spendingTrendColor = spendingTrendDirection === 'up'
    ? 'text-rose-600'
    : spendingTrendDirection === 'down'
      ? 'text-emerald-600'
      : 'text-slate-500';
  const monthOverMonthComparison = useMemo(() => {
    return Array.isArray(insightDetails.monthOverMonthComparison)
      ? insightDetails.monthOverMonthComparison
      : [];
  }, [insightDetails.monthOverMonthComparison]);
  const budgetVsActualComparison = useMemo(() => {
    return Array.isArray(insightDetails.budgetVsActualByCategory)
      ? insightDetails.budgetVsActualByCategory
      : [];
  }, [insightDetails.budgetVsActualByCategory]);
  const budgetVsActualTotals = useMemo(() => {
    return budgetVsActualComparison.reduce((accumulator, entry) => {
      const budget = Number(entry?.budget || 0);
      const actual = Number(entry?.actual || 0);
      const savings = Number(entry?.difference || 0);
      return {
        budget: accumulator.budget + budget,
        actual: accumulator.actual + actual,
        savings: accumulator.savings + savings,
      };
    }, { budget: 0, actual: 0, savings: 0 });
  }, [budgetVsActualComparison]);
  const updateRecurringReminderSetting = useCallback((seriesId, changes) => {
    if (!seriesId) {
      return;
    }
    setReminderSettingsBySeries((current) => {
      const previous = current[seriesId] || {};
      return {
        ...current,
        [seriesId]: {
          daysBefore: Number(previous.daysBefore || 1),
          enabled: Boolean(previous.enabled),
          reminderDays: getSelectedReminderDays(previous),
          ...changes,
        },
      };
    });
  }, []);

  const topWheelCategories = useMemo(() => {
    return categoryData.slice(0, 7);
  }, [categoryData]);

  const wheelChartData = useMemo(() => {
    const topSevenTotal = topWheelCategories.reduce((sum, category) => sum + Number(category.value || 0), 0);
    const totalSpent = Number(summary.totalSpent || 0);
    const otherValue = Math.max(0, Number((totalSpent - topSevenTotal).toFixed(2)));

    if (otherValue <= 0) {
      return topWheelCategories;
    }

    return [
      ...topWheelCategories,
      {
        id: 'other-categories',
        name: 'Other',
        color: '#cbd5e1',
        value: otherValue,
        count: 0,
        percentage: totalSpent > 0 ? (otherValue / totalSpent) * 100 : 0,
        hiddenFromLegend: true,
      },
    ];
  }, [summary.totalSpent, topWheelCategories]);

  const topMonthOverMonthComparison = useMemo(() => {
    return [...monthOverMonthComparison].slice(0, 5);
  }, [monthOverMonthComparison]);

  const chartSizing = useMemo(() => {
    if (isSmallScreen) {
      return {
        containerHeightClass: 'h-[260px]',
        innerRadius: 62,
        outerRadius: 102,
      };
    }

    return {
      containerHeightClass: 'h-[340px] md:h-[390px]',
      innerRadius: 92,
      outerRadius: 140,
    };
  }, [isSmallScreen]);

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center">
        <div className="text-center">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-slate-400 border-t-transparent rounded-full mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br from-sky-100 via-rose-50 to-amber-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] ${
      isCondensedCollapsedView ? 'rounded-2xl p-3 md:p-5' : 'rounded-3xl p-4 md:p-8'
    }`}>
      <div className="pointer-events-none absolute -top-16 -left-20 h-64 w-64 rounded-full bg-pink-200/40 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-28 h-72 w-72 rounded-full bg-cyan-200/35 blur-3xl" />

      <div className={`relative ${isCondensedCollapsedView ? 'space-y-2.5 md:space-y-3' : 'space-y-5 md:space-y-7'}`}>
        <section>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className={`${isCondensedCollapsedView ? 'text-2xl sm:text-2xl md:text-3xl' : 'text-2xl sm:text-3xl md:text-4xl'} font-semibold tracking-tight text-slate-900`} style={{ letterSpacing: '-0.02em' }}>Dashboard</h1>
              {!isCondensedCollapsedView && (
                <p className="text-slate-600 mt-1 text-sm sm:text-base md:text-lg">Overview of your spending</p>
              )}
            </div>
              <div
                ref={monthPickerRef}
                className="relative rounded-lg border border-slate-300/70 bg-white/80 backdrop-blur px-1.5 py-1.5 text-sm text-slate-700 shadow-sm sm:rounded-xl sm:px-2 sm:py-2"
              >
                <div className="inline-flex items-center gap-0.5 sm:gap-1">
                  <button
                    type="button"
                    onClick={() => changeMonthBy(-1)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-700 text-base font-semibold transition hover:bg-slate-100 hover:text-slate-900 sm:h-10 sm:w-10 sm:rounded-xl sm:text-lg"
                    aria-label="Previous month"
                  >
                    <span aria-hidden="true">&lt;</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (isMonthPickerOpen) {
                        setIsMonthPickerOpen(false);
                      } else {
                        openMonthPicker();
                      }
                    }}
                    className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-800 whitespace-nowrap transition hover:bg-slate-100 sm:px-3 sm:py-1.5 sm:text-sm"
                    aria-label="Open month picker"
                    aria-expanded={isMonthPickerOpen}
                  >
                    {formatMonthLabel(selectedMonth)}
                  </button>

                  <button
                    type="button"
                    onClick={() => changeMonthBy(1)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-700 text-base font-semibold transition hover:bg-slate-100 hover:text-slate-900 sm:h-10 sm:w-10 sm:rounded-xl sm:text-lg"
                    aria-label="Next month"
                  >
                    <span aria-hidden="true">&gt;</span>
                  </button>
                </div>

              {isMonthPickerOpen && (
                  <div className="mt-2 w-[20rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                    <div className="mb-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setPickerYear((current) => current - 1)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                        aria-label="Previous year"
                      >
                        <span aria-hidden="true">&lt;</span>
                      </button>
                      <p className="text-sm font-semibold text-slate-900">{pickerYear}</p>
                      <button
                        type="button"
                        onClick={() => setPickerYear((current) => current + 1)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                        aria-label="Next year"
                      >
                        <span aria-hidden="true">&gt;</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {MONTH_OPTIONS.map((month) => {
                        const candidate = `${pickerYear}-${String(month.value).padStart(2, '0')}`;
                        const isActive = candidate === selectedMonth;
                        return (
                          <button
                            key={`picker-${pickerYear}-${month.value}`}
                            type="button"
                            onClick={() => handleMonthGridSelect(month.value)}
                            className={`rounded-lg px-2 py-2 text-sm font-medium transition ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            {month.label.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
          </div>
          <div className={`${isCondensedCollapsedView ? 'mt-2' : 'mt-3'} flex justify-end`}>
            <button
              type="button"
              onClick={toggleAllSectionsCollapsed}
              className={`${isCondensedCollapsedView
                ? 'inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-gradient-to-r from-sky-50 to-indigo-50 px-2.5 py-1 text-xs font-semibold text-sky-700 shadow-sm transition-all duration-200 hover:from-sky-100 hover:to-indigo-100 hover:shadow'
                : 'inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-gradient-to-r from-sky-50 to-indigo-50 px-3 py-1.5 text-sm font-semibold text-sky-700 shadow-sm transition-all duration-200 hover:from-sky-100 hover:to-indigo-100 hover:shadow'
              }`}
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                className={`h-3.5 w-3.5 transition-transform ${areAllSectionsCollapsed ? '' : 'rotate-90'}`}
                aria-hidden="true"
              >
                <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {areAllSectionsCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
          </div>
        </section>

        <UpcomingRemindersSection
          sectionRef={upcomingRemindersSectionRef}
          isCollapsed={isUpcomingRemindersCollapsed}
          handleSectionCardClick={handleSectionCardClick}
          renderSectionToggle={renderSectionToggle}
          upcomingRecurring={upcomingRecurring}
          upcomingRecurringLoading={upcomingRecurringLoading}
          upcomingRecurringError={upcomingRecurringError}
          reminderSettingsBySeries={reminderSettingsBySeries}
          reminderActionsByOccurrence={reminderActionsByOccurrence}
          setReminderActionsByOccurrence={setReminderActionsByOccurrence}
          updateRecurringReminderSetting={updateRecurringReminderSetting}
          upcomingView={upcomingView}
          setUpcomingView={setUpcomingView}
          selectedMonth={selectedMonth}
          selectedMonthNumber={selectedMonthNumber}
          selectedYear={selectedYear}
          setCollapsedSections={setCollapsedSections}
          recordRecurringPayment={recordRecurringPayment}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
        />

        {SHOW_SPENDING_AI && (
          <section
            ref={assistantSectionRef}
            onClick={(event) => handleSectionCardClick('assistant', event)}
            className={`group relative overflow-hidden border border-white/80 bg-gradient-to-br from-white via-blue-50/90 to-cyan-100/55 backdrop-blur shadow-[0_14px_34px_-24px_rgba(14,116,144,0.55)] transition-all duration-200 ${isAssistantCollapsed ? 'rounded-2xl p-3 md:p-4 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-24px_rgba(14,116,144,0.6)]' : 'rounded-3xl p-5 md:p-6'}`}
          >
            <div className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/90 to-transparent" />
            <div className={`${isAssistantCollapsed ? 'mb-0' : 'mb-4'} flex items-center justify-between gap-3`}>
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="h-6 w-2 rounded-full bg-gradient-to-b from-cyan-500 to-blue-500 shadow-[0_0_0_2px_rgba(255,255,255,0.7)]" aria-hidden="true" />
                <h2 className={`${isAssistantCollapsed ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'} font-semibold tracking-tight text-slate-900`}>Ask Spending AI</h2>
              </div>
              {renderSectionToggle('assistant', isAssistantCollapsed, 'Ask Spending AI')}
            </div>
            {!isAssistantCollapsed && (
              <form onSubmit={handleAssistantLauncherSubmit}>
                <label htmlFor="dashboard-ai-launcher" className="sr-only">Ask Spending AI</label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 inline-flex items-center text-slate-400">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M9.5 3a6.5 6.5 0 104.123 11.527l2.925 2.925a1 1 0 001.414-1.414l-2.925-2.925A6.5 6.5 0 009.5 3zm-4.5 6.5a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-3 inline-flex items-center">
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
                        <path d="M10 2a1 1 0 011 1v1.07A5.002 5.002 0 0115 9v2.5a2.5 2.5 0 01-2.5 2.5h-5A2.5 2.5 0 015 11.5V9a5.002 5.002 0 014-4.93V3a1 1 0 011-1zM7 9a3 3 0 016 0v2.5a.5.5 0 01-.5.5h-5a.5.5 0 01-.5-.5V9zm2.5 6h1a1.5 1.5 0 010 3h-1a1.5 1.5 0 010-3z" />
                      </svg>
                      AI
                    </span>
                  </span>
                  <input
                    id="dashboard-ai-launcher"
                    type="text"
                    value={aiSearchQuery}
                    onChange={(event) => setAiSearchQuery(event.target.value)}
                    onFocus={() => openAssistantPage(aiSearchQuery)}
                    placeholder="Ask about this month or past months..."
                    className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-20 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </form>
            )}
          </section>
        )}

        <KeyInsightsSection
          sectionRef={keyInsightsSectionRef}
          isCollapsed={isKeyInsightsCollapsed}
          handleSectionCardClick={handleSectionCardClick}
          renderSectionToggle={renderSectionToggle}
          spendingByCategorySectionRef={spendingByCategorySectionRef}
          navigate={navigate}
          selectedMonth={selectedMonth}
          summary={summary}
          hasPreviousMonthSpent={hasPreviousMonthSpent}
          monthOverMonth={monthOverMonth}
          recurringVsFlexible={recurringVsFlexible}
          budgetRingAccent={budgetRingAccent}
          budgetUsageForRing={budgetUsageForRing}
          hasConfiguredBudget={hasConfiguredBudget}
          totalBudget={totalBudget}
          totalSpent={totalSpent}
          budgetDifferenceColor={budgetDifferenceColor}
          budgetSavingsLabel={budgetSavingsLabel}
          spendingTrendColor={spendingTrendColor}
          spendingTrendArrow={spendingTrendArrow}
          spendingDelta={spendingDelta}
          recurringTransactions={recurringTransactions}
          flexibleTransactions={flexibleTransactions}
          recurringCategorySummary={recurringCategorySummary}
          flexibleCategorySummary={flexibleCategorySummary}
          recurringCategoryTotal={recurringCategoryTotal}
          flexibleCategoryTotal={flexibleCategoryTotal}
          topCategoryDetail={topCategoryDetail}
          budgetVsActualComparison={budgetVsActualComparison}
          budgetVsActualTotals={budgetVsActualTotals}
          transactionsMonthLink={transactionsMonthLink}
          formatCurrency={formatCurrency}
          formatCurrencyRounded={formatCurrencyRounded}
          formatSavingsRounded={formatSavingsRounded}
          formatDate={formatDate}
        />

        <SpendingByCategorySection
          sectionRef={spendingByCategorySectionRef}
          isCollapsed={isSpendingByCategoryCollapsed}
          handleSectionCardClick={handleSectionCardClick}
          renderSectionToggle={renderSectionToggle}
          topWheelCategories={topWheelCategories}
          chartSizing={chartSizing}
          wheelChartData={wheelChartData}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          summaryTotalSpent={summary.totalSpent}
          isSmallScreen={isSmallScreen}
          selectedMonth={selectedMonth}
          transactionsMonthLink={transactionsMonthLink}
        />

        <section
          ref={monthOverMonthSectionRef}
          onClick={(event) => handleSectionCardClick('month-over-month', event)}
          className={`group relative overflow-hidden border border-white/80 bg-gradient-to-br from-white via-emerald-50/90 to-lime-100/55 backdrop-blur shadow-[0_14px_34px_-24px_rgba(16,185,129,0.45)] transition-all duration-200 md:hover:-translate-y-1 md:hover:shadow-xl ${isMonthOverMonthCollapsed ? 'rounded-2xl p-3 md:p-4 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-24px_rgba(16,185,129,0.52)]' : 'rounded-3xl p-5 md:p-6'}`}
        >
          <div className="pointer-events-none absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/90 to-transparent" />
          <div className={`${isMonthOverMonthCollapsed ? 'mb-0' : 'mb-4'} flex items-center justify-between gap-3`}>
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="h-6 w-2 rounded-full bg-gradient-to-b from-emerald-500 to-lime-500 shadow-[0_0_0_2px_rgba(255,255,255,0.7)]" aria-hidden="true" />
              <h3 className={`${isMonthOverMonthCollapsed ? 'text-base sm:text-lg' : 'text-lg sm:text-2xl'} font-semibold leading-tight tracking-tight text-slate-900`}>{isMonthOverMonthCollapsed ? 'MoM Category' : 'Month over Month by Category'}</h3>
            </div>
            {renderSectionToggle('month-over-month', isMonthOverMonthCollapsed, 'Month over Month')}
          </div>

          {!isMonthOverMonthCollapsed && (topMonthOverMonthComparison.length > 0 ? (
            <>
              <div className="sm:hidden">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col className="w-[28%]" />
                    <col className="w-[25%]" />
                    <col className="w-[25%]" />
                    <col className="w-[25%]" />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="py-2 text-left">Category</th>
                      <th className="py-2 pl-2 text-right">Cur</th>
                      <th className="py-2 pl-2 text-right">Prev</th>
                      <th className="py-2 pl-2 text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topMonthOverMonthComparison.map((entry) => {
                      const difference = Number(entry.difference || 0);
                      const roundedDifference = Math.round(difference);
                      const differenceColor = roundedDifference > 0
                        ? 'text-rose-600'
                        : roundedDifference < 0
                          ? 'text-emerald-600'
                          : 'text-slate-500';
                      const changeArrow = roundedDifference > 0 ? '↑' : roundedDifference < 0 ? '↓' : '→';
                      return (
                        <tr key={`mom-top-mobile-${entry.id}`} className="border-b border-slate-100 text-sm">
                          <td className="py-2.5 pr-2 align-top font-medium text-slate-900 break-words leading-snug">{renderCategoryLabel(entry.name)}</td>
                          <td className="py-2.5 pl-2 text-right whitespace-nowrap text-slate-700 tabular-nums">{formatCurrencyRounded(entry.currentValue)}</td>
                          <td className="py-2.5 pl-2 text-right whitespace-nowrap text-slate-600 tabular-nums">{formatCurrencyRounded(entry.previousValue)}</td>
                          <td className={`py-2.5 pl-2 text-right whitespace-nowrap font-semibold tabular-nums ${differenceColor}`}>
                            {changeArrow} {formatCurrencyRounded(Math.abs(roundedDifference))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-sm uppercase tracking-wide text-slate-500">
                    <th className="py-3 pr-3 font-semibold">Category</th>
                    <th className="py-3 px-3 font-semibold text-right">Current Month</th>
                    <th className="py-3 px-3 font-semibold text-right">Previous Month</th>
                    <th className="py-3 pl-3 font-semibold text-right">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {topMonthOverMonthComparison.map((entry) => {
                    const difference = Number(entry.difference || 0);
                    const roundedDifference = Math.round(difference);
                    const differenceColor = roundedDifference > 0
                      ? 'text-rose-600'
                      : roundedDifference < 0
                        ? 'text-emerald-600'
                        : 'text-slate-500';
                    const changeArrow = roundedDifference > 0 ? '↑' : roundedDifference < 0 ? '↓' : '→';
                    return (
                      <tr key={`mom-top-${entry.id}`} className="border-b border-slate-100">
                        <td className="py-3 pr-3 font-medium text-slate-900 leading-snug">{renderCategoryLabel(entry.name)}</td>
                        <td className="py-3 px-3 text-right text-slate-800">{formatCurrencyRounded(entry.currentValue)}</td>
                        <td className="py-3 px-3 text-right text-slate-700">{formatCurrencyRounded(entry.previousValue)}</td>
                        <td className={`py-3 pl-3 text-right font-semibold ${differenceColor}`}>
                          {changeArrow} {formatCurrencyRounded(Math.abs(roundedDifference))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-slate-500">No category comparison data available.</p>
          ))}
          {!isMonthOverMonthCollapsed && (
            <div className="mt-3 flex justify-end">
              <Link
                to={`/dashboard/category-comparison?month=${selectedMonth}`}
                className="text-sm font-medium lowercase text-blue-700 hover:text-blue-800"
              >
                view all
              </Link>
            </div>
          )}
        </section>

        <RecentTransactionsSection
          sectionRef={recentTransactionsSectionRef}
          isCollapsed={isRecentTransactionsCollapsed}
          handleSectionCardClick={handleSectionCardClick}
          renderSectionToggle={renderSectionToggle}
          recentTransactions={recentTransactions}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
        />

      </div>
      <StatusToast
        error={error}
        onClearError={() => setError('')}
      />
    </div>
  );
}

export default Dashboard;
